import { useAppDispatch } from "@/hooks/redux";
import { useProfileQuery } from "@/core/auth/services";
import { useConfidenceReportVisibility } from "@/modules/analytics/hooks/useConfidenceReportVisibility";
import {
  CONFIDENCE_REPORT_POLL_MS,
  isReportActive,
  useConfidenceReport,
  useGetConfidenceReportRegionsQuery,
} from "@/modules/analytics/services/confidenceReportApi";
import { useGetTasksQuery } from "@/modules/capture/services/captureApi";
import { useActiveProject } from "@/modules/project/hooks/useActiveProject";
import {
  projectApi,
  ProjectApiTagType,
  useCreateProjectLabelMutation,
  useArchiveProjectLabelMutation,
  useRestoreProjectLabelMutation,
  useDeleteVisionNoteMutation,
  useGetPreAnnotateSettingsQuery,
  useGetProjectLabelsQuery,
  useUpdatePreAnnotateSettingsMutation,
  useSuggestLabelDescriptionMutation,
  useUpdateProjectLabelMutation,
  useUpdateVisionNoteMutation,
} from "@/modules/project/services/projectApi";
import {
  ConfidenceReportGtGeometryEnum,
  ConfidenceReportStatusEnum,
  DEFAULT_PRE_ANNOTATE_CONFIG,
  hasUsableDescriptions,
  AutoAnnotationBatchItemStatusEnum,
  AutoAnnotationBatchStatusEnum,
  Label,
  PreAnnotateModelTypeEnum,
  PredictedRectangle,
  TaskDetail,
  UpdatePreAnnotateSettingsDocument,
  UpdateTask,
  VisionStreamPhaseEnum,
  VisionTranscriptTurn,
} from "@repo/schema";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAnnotationNudge } from "../../hooks/useAnnotationNudge";
import { useCanvasState } from "../../hooks/useCanvasState";
import { useHistory } from "../../hooks/useHistory";
import { useLabelNavigationGuard } from "../../hooks/useLabelNavigationGuard";
import { useLabelShortcuts } from "../../hooks/useLabelShortcuts";
import { DEFAULT_SORT, useLabelUrlState } from "../../hooks/useLabelUrlState";
import { useBatchAutoAnnotate } from "../../hooks/useBatchAutoAnnotate";
import { useTaskSelection } from "../../hooks/useTaskSelection";
import { useToolMode } from "../../hooks/useToolMode";
import { useVisionStream } from "../../hooks/useVisionStream";
import {
  useLazyGetVisionTranscriptQuery,
  useGetTaskQuery,
  useSaveVisionTurnNoteMutation,
  useUpdateTaskMutation,
} from "../../services/labelApi";
import { Annotation } from "../../types/annotations";
import {
  PreAnnotateRunOutcome,
  PreAnnotateRunView,
  VisionSavedNote,
  VisionSession,
} from "../../types/preAnnotate";
import {
  annotationsToUpdatePayload,
  INFERRED_ID_PREFIX,
  inferredAnnotationId,
  isPredictedAnnotation,
  isStagedAnnotation,
  isStagedId,
  mapPredictedToStaged,
  polygonAnnotationToBbox,
  regionsToAnnotations,
  splitStagedAnnotations,
  stagedAnnotationId,
  taskDetailToAnnotations,
} from "../../utils/mapAnnotations";
import { pickExampleRegion } from "../../utils/pickExampleRegion";
import {
  addToGroup,
  assignPasteGroups,
  canGroupSelection,
  groupSelected,
  removeFromGroup,
  ungroupAnnotations,
} from "../../utils/groupAnnotations";
import { AnnotationPanel } from "../AnnotationPanel";
import { thoughtHeadline } from "../../utils/thoughtHeadline";
import { Canvas, CanvasHandle } from "../Canvas";
import type { PreviewBoxSpec } from "../Canvas/KonvaStage";
import { ClassSelect, type LabelEditorInput } from "../ClassSelect";
import type { GenerateLabelDescription } from "../GenerateDescriptionButton";
import { DataSourcePanel } from "../DataSourcePanel";
import { isMetricSort } from "../TaskSortFilterDialog";
import { LeaveAnnotationsDialog } from "../LeaveAnnotationsDialog";
import { ReplaceAutoAnnotationsDialog } from "../ReplaceAutoAnnotationsDialog";
import { AwaitingReviewBanner } from "../AwaitingReviewBanner";
import { batchSkipNote } from "../BatchAutoAnnotateDialog";
import { BatchRunCard } from "../BatchRunCard";
import type {
  TaskFilterState,
  TaskSortState,
} from "../TaskSortFilterDialog";
import {
  DETACHED_FAILED,
  DETACHED_RUNNING,
  NOTE_REMOVE_FAILED,
  NOTE_SAVE_FAILED,
  NOTE_UPDATE_FAILED,
  PreAnnotateRunPanel,
} from "../PreAnnotateRunPanel";
import {
  PreAnnotateSettingsDialog,
  VISION_SEGMENTATION_SOON,
  VISION_UNAVAILABLE,
} from "../PreAnnotateSettingsDialog";
import { Toolbar } from "../Toolbar";
import { apiErrorMessage } from "@/core/api";
import { tKey } from "@/core/i18n";

const TASKS_PER_PAGE = 50;

// ── Auto-annotate run panel helpers ───────────────────────────────────────────
// The panel above the labels bar shows the open task's run. LabelPage owns
// every task's run state; these are the pure pieces of the wiring.

/**
 * One task's single-image auto-annotation, as its run card shows it.
 *
 * `previewBoxes` are the boxes a streamed Robopipe Vision turn has emitted so
 * far. Kept apart from `annotations` on purpose: they arrive one at a time out
 * of JSON that is still being written, so folding them into the canvas state
 * would give the labeler an undo entry per box, a dirty flag that flickers
 * mid-run, and a selection cleared under their cursor. They render on their
 * own non-listening Konva layer and are dropped the instant the turn's real
 * result lands. Kept per task, so a labeler who comes back mid-run sees every
 * box streamed while they were away.
 *
 * `detached` marks a run recovered from the server's turn lock (after a
 * reload, say) rather than streamed by this page: no Cancel, no previews.
 * `turnBefore` is the conversation's turn count when it was recovered — how
 * its end tells a recorded turn from a failed one.
 */
type TaskRun = {
  view: PreAnnotateRunView;
  previewBoxes: PreviewBoxSpec[];
  detached?: { turnBefore: number };
};

/** The request behind a live run, and which run it is. */
type RunRequest = { runId: number; abort: () => void };

const NO_PREVIEW_BOXES: PreviewBoxSpec[] = [];

/** How often task detail is re-read while a recovered run is on screen. */
const DETACHED_POLL_MS = 5_000;

/**
 * How far a server-stamped turn lock may lead the client clock and still be
 * a run this page already stopped following (see `releasedAtRef`).
 */
const RELEASE_CLOCK_SKEW_MS = 30_000;

/** A finished Robopipe Vision turn, as the run card reports it. */
const visionTurnOutcome = (
  count: number,
  summary: string | undefined,
  turn: number,
): PreAnnotateRunOutcome => {
  const modelType = PreAnnotateModelTypeEnum.DETECTION;
  return count === 0
    ? { kind: "empty", modelType, summary, turn }
    : { kind: "done", count, modelType, summary, turn };
};

/** Why the ✨ button, Run again and Send are greyed out mid-run. */
const PRE_ANNOTATE_BUSY_REASON = tKey("label:page.busy");
/**
 * Why ✨ is greyed out on an image a live batch has claimed.
 *
 * Only on the images the batch has NOT finished yet: a lone run and the
 * batch's run both stage with `replaceForTask`, which deletes the task's whole
 * staged set before inserting, so a single run racing the batch's run on the
 * same image leaves half a result. Images outside the batch stay fully usable
 * — a labeller should not be locked out of the rest of the dataset for the
 * duration.
 */
const BATCH_CLAIMS_IMAGE_REASON = tKey("label:page.batchClaimsImage");

// Stable empty selection used to disable GT-selection-driven behavior
// (e.g. keyboard nudge) while the read-only inferred view is active.
const EMPTY_SELECTION = new Set<string>();

/** How often the task list is re-read while some image has an auto-annotation in flight. */
const RUNNING_LIST_POLL_MS = 5_000;

export const LabelPage = () => {
  const { t } = useTranslation(["label", "common"]);
  const [activeProject] = useActiveProject();
  const projectId = activeProject?.id;

  const {
    selectedTaskId,
    setSelectedTaskId,
    page,
    setPage,
    filter,
    sort,
    setSort,
    setSortAndFilter,
    pendingAnchorRef,
  } = useLabelUrlState();

  // ─── Batch auto-annotation ─────────────────────────────────────────────────
  const selection = useTaskSelection();
  // Destructured because useTaskSelection returns a fresh object each render
  // while its callbacks are stable — depending on `selection` itself would
  // defeat every useCallback below.
  const {
    selectedIds,
    toggle: toggleSelection,
    togglePage: toggleSelectionPage,
    clear: clearSelection,
  } = selection;
  // Which finished batch the operator has waved away. Ids rather than a
  // boolean so the next batch's card is not pre-dismissed.
  const [dismissedBatchId, setDismissedBatchId] = useState<number | null>(null);
  // Staged sets whose awaiting-review banner was waved away, keyed by the
  // set's first auto-annotation id: a fresh run on the same image stages new
  // rows, so its banner comes back.
  const [dismissedReviewIds, setDismissedReviewIds] = useState<
    ReadonlySet<number>
  >(() => new Set());
  // Same for an empty run, which has no rows to key on: by task instead.
  const [dismissedEmptyTaskIds, setDismissedEmptyTaskIds] = useState<
    ReadonlySet<number>
  >(() => new Set());

  // ─── Inferred-regions tab ──────────────────────────────────────────────────
  // Declared early so queries below can reference activeAnnotationTab.
  const [activeAnnotationTab, setActiveAnnotationTab] = useState<
    "labels" | "history" | "inferred"
  >("labels");
  const [showGtOverlay, setShowGtOverlay] = useState(false);

  // "Show in dataset" preference — gates all confidence-report-derived
  // per-task UI (task-card metric badges, Inferred tab, region IoU pills).
  const { showInDataset } = useConfidenceReportVisibility();

  // Single-select highlight for inferred regions ("inferred-<regionId>").
  // Kept separate from selectedAnnotationIds so inferred ids never enter the
  // GT selection machinery (nudge, delete, group, copy, save payloads).
  const [selectedInferredId, setSelectedInferredId] = useState<string | null>(
    null,
  );

  const handleInferredSelect = useCallback((id: string | null) => {
    setSelectedInferredId((prev) => {
      if (id === null) return null;
      // GT overlay shapes are not selectable in the inferred view.
      if (!id.startsWith(INFERRED_ID_PREFIX)) return prev;
      return prev === id ? null : id;
    });
  }, []);

  const handleSelectInferredRegion = useCallback(
    (regionId: number) => handleInferredSelect(inferredAnnotationId(regionId)),
    [handleInferredSelect],
  );

  const inferredSelectionSet = useMemo(
    () => (selectedInferredId ? new Set([selectedInferredId]) : new Set<string>()),
    [selectedInferredId],
  );
  const selectedInferredRegionId = selectedInferredId
    ? Number(selectedInferredId.slice(INFERRED_ID_PREFIX.length))
    : null;

  // Track the confidence report, polling while a run is active, so metric
  // availability and task polling react to runs without a page refresh.
  const confidenceReportQuery = useConfidenceReport(projectId);
  const confidenceReport = confidenceReportQuery.data;
  const metricsAvailable =
    confidenceReport?.status === ConfidenceReportStatusEnum.DONE;

  // Drop an applied metric sort once the report is known not to be DONE —
  // starting a new run wipes the per-task metrics, so the sort would target
  // empty columns while the sort dialog no longer offers it.
  const reportResolved =
    !confidenceReportQuery.isUninitialized && !confidenceReportQuery.isLoading;
  useEffect(() => {
    if (reportResolved && !metricsAvailable && isMetricSort(sort.sortBy)) {
      setSort(DEFAULT_SORT);
    }
  }, [reportResolved, metricsAvailable, sort.sortBy, setSort]);

  // Leave the Inferred tab when "Show in dataset" is switched off — the tab
  // trigger disappears, so a controlled Tabs stuck on "inferred" would render
  // an empty panel.
  useEffect(() => {
    if (!showInDataset && activeAnnotationTab === "inferred") {
      setActiveAnnotationTab("labels");
      setSelectedInferredId(null);
    }
  }, [showInDataset, activeAnnotationTab]);

  const {
    data: tasksData,
    isFetching: isFetchingTasks,
    refetch: refetchTasks,
  } = useGetTasksQuery(
    {
      projectId: projectId!,
      page,
      limit: TASKS_PER_PAGE,
      ...(filter.annotationFilter !== "all" && {
        annotated: filter.annotationFilter,
      }),
      ...(filter.labelIds.length > 0 && {
        labelIds: filter.labelIds.join(","),
      }),
      ...(filter.updatedBy.length > 0 && {
        updatedBy: filter.updatedBy.join(","),
      }),
      ...(filter.reviewFilter !== "all" && {
        awaitingReview: filter.reviewFilter,
      }),
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    },
    {
      skip: !projectId,
      refetchOnMountOrArgChange: true,
      // While a confidence report is running, poll tasks so metric badges
      // stream in as the batch job processes each chunk.
      pollingInterval: isReportActive(confidenceReport)
        ? CONFIDENCE_REPORT_POLL_MS
        : 0,
    },
  );
  const tasks = tasksData?.data ?? [];
  // A run started elsewhere (another tab, a batch, before a reload) has no
  // event to tell this list it finished, so re-read it while any row says it
  // is still running. The chip clears itself when the turn lock does.
  const anyTaskRunning = tasks.some((task) => task.running);
  useEffect(() => {
    if (!anyTaskRunning) return;
    const timer = setInterval(() => void refetchTasks(), RUNNING_LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [anyTaskRunning, refetchTasks]);
  const totalPages = tasksData
    ? Math.ceil(tasksData.total / tasksData.limit)
    : 0;

  const { data: labels = [], isLoading: isLoadingLabels } =
    useGetProjectLabelsQuery({ projectId: projectId! }, { skip: !projectId });
  const [createProjectLabel] = useCreateProjectLabelMutation();
  const [updateProjectLabel] = useUpdateProjectLabelMutation();
  const [archiveProjectLabel] = useArchiveProjectLabelMutation();
  const [restoreProjectLabel] = useRestoreProjectLabelMutation();

  // Whenever there's no task in the URL but the current page has tasks,
  // pick one. Covers two cases:
  //   - Initial load (no `?task=…`) → pick the first task.
  //   - After a page change (`setPage` clears the task) → pick first or
  //     last depending on the queued anchor.
  // Gated on `!isFetchingTasks` so we never anchor on the *previous*
  // page's data — RTK Query keeps `data` around while refetching.
  useEffect(() => {
    if (selectedTaskId !== null || isFetchingTasks || tasks.length === 0)
      return;
    const targetId =
      pendingAnchorRef.current === "last"
        ? tasks[tasks.length - 1].id
        : tasks[0].id;
    setSelectedTaskId(targetId, { replace: true });
    pendingAnchorRef.current = null;
  }, [
    selectedTaskId,
    isFetchingTasks,
    tasks,
    setSelectedTaskId,
    pendingAnchorRef,
  ]);

  const selectedTask = tasks.find((t) => t.id === selectedTaskId);

  const { data: taskDetail } = useGetTaskQuery(
    { projectId: projectId!, taskId: selectedTaskId! },
    { skip: !projectId || selectedTaskId === null },
  );

  // Inferred regions for the selected task — fetched while on the Labels or Inferred tab
  // so the IoU map is available for GT region rows and for the canvas/inferred list.
  const { data: rawRegions = [], isLoading: isLoadingRegions } =
    useGetConfidenceReportRegionsQuery(
      { projectId: projectId!, taskId: selectedTaskId! },
      {
        skip:
          !showInDataset ||
          activeAnnotationTab === "history" ||
          !projectId ||
          selectedTaskId === null,
        // Poll while the report is running so regions stream in.
        pollingInterval: isReportActive(confidenceReport)
          ? CONFIDENCE_REPORT_POLL_MS
          : 0,
      },
    );

  const inferredAnnotations = useMemo(
    () => regionsToAnnotations(rawRegions),
    [rawRegions],
  );

  const [updateTask] = useUpdateTaskMutation();
  const { data: profile } = useProfileQuery();

  const [preAnnotateOpen, setPreAnnotateOpen] = useState(false);

  const [
    updatePreAnnotateSettingsMutation,
    { isLoading: isSavingPreAnnotateSettings },
  ] = useUpdatePreAnnotateSettingsMutation();

  // One document for the whole feature: which mode the ✨ button runs, the
  // label descriptions and whether the server can run Robopipe Vision at all.
  // Availability is read from configuration server-side, not probed.
  const {
    data: preAnnotateSettings,
    isLoading: isLoadingPreAnnotateSettings,
  } = useGetPreAnnotateSettingsQuery(
    { projectId: projectId! },
    { skip: !projectId },
  );

  // Robopipe Vision is the only engine. Until the document arrives nothing is
  // runnable: the server is not known to be configured and no label is known
  // to be described.
  const labelDescriptions = preAnnotateSettings?.labelDescriptions;
  const visionAvailable =
    preAnnotateSettings?.availability.visionAvailable === true;
  // The API stores Vision's default mode as detection (it detects boxes only
  // for now), so this is detection in practice; read from the document so the
  // segmentation plumbing below lights up once the API allows it.
  const preAnnotateDefaultMode =
    preAnnotateSettings?.config.defaultMode ??
    DEFAULT_PRE_ANNOTATE_CONFIG.defaultMode;

  /**
   * The live batch. `refetchTasks` runs once when it settles — standing in for
   * the forty per-run list invalidations that `invalidateAfterRun` suppresses
   * while a batch is in flight.
   */
  const handleBatchSettled = useCallback(() => {
    refetchTasks();
  }, [refetchTasks]);
  const batch = useBatchAutoAnnotate(projectId, handleBatchSettled);
  const batchRunning = batch.batch?.status === AutoAnnotationBatchStatusEnum.RUNNING;
  const visibleBatch =
    batch.batch && batch.batch.id !== dismissedBatchId ? batch.batch : null;

  const pageIds = useMemo(() => tasks.map((task) => task.id), [tasks]);

  const handleToggleSelect = useCallback(
    (taskId: number, shiftKey: boolean) => toggleSelection(taskId, shiftKey, pageIds),
    [toggleSelection, pageIds],
  );
  const handleToggleSelectPage = useCallback(
    () => toggleSelectionPage(pageIds),
    [toggleSelectionPage, pageIds],
  );

  /**
   * Changing the filter or the sort drops the selection.
   *
   * It survives paging on purpose — walking pages to build one batch is the
   * point — but a filter change replaces the set the selection was drawn from,
   * and silently running images the operator can no longer see would be the
   * worst kind of surprise.
   */
  const handleSortFilterChange = useCallback(
    (next: { sort: TaskSortState; filter: TaskFilterState }) => {
      clearSelection();
      setSortAndFilter(next);
    },
    [clearSelection, setSortAndFilter],
  );

  /**
   * Starts straight away, no confirm step. Also the "Retry failed" handler,
   * which passes exactly the failed ids rather than the selection.
   */
  const handleRunBatch = useCallback(async (taskIds: number[]) => {
    if (batch.isStarting) return;
    try {
      const created = await batch.start(taskIds);
      clearSelection();
      const skipped =
        created.skippedAnnotated + created.skippedAwaitingReview;
      if (skipped > 0) {
        toast.info(
          batchSkipNote(t, created.skippedAnnotated, created.skippedAwaitingReview) ?? "",
        );
      }
    } catch (error) {
      // The API refuses a batch for reasons the operator can act on — the
      // project has no described labels, Vision is not configured, or somebody
      // else is already running one. The selection is kept so the operator can fix and re-run.
      toast.error(apiErrorMessage(error, t("page.startBatchFailed")));
    }
  }, [batch, clearSelection, t]);

  // One-time cleanup: remove old per-user localStorage keys from before
  // settings were centralised in the DB.
  useEffect(() => {
    if (!profile?.id || !projectId) return;
    try {
      localStorage.removeItem(`preAnnotateSettings:${profile.id}:${projectId}`);
    } catch {
      // ignore
    }
  }, [profile?.id, projectId]);

  const updatePreAnnotateSettings = useCallback(
    async (body: UpdatePreAnnotateSettingsDocument): Promise<void> => {
      if (!projectId) return;
      await updatePreAnnotateSettingsMutation({ projectId, body }).unwrap();
    },
    [projectId, updatePreAnnotateSettingsMutation],
  );

  const { toolMode, setToolMode } = useToolMode();
  const [showCrosshair, setShowCrosshair] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("label.crosshair") === "1";
  });
  const toggleCrosshair = useCallback(() => {
    setShowCrosshair((prev) => {
      const next = !prev;
      window.localStorage.setItem("label.crosshair", next ? "1" : "0");
      return next;
    });
  }, []);
  const canvasRef = useRef<CanvasHandle>(null);
  const handleResetView = useCallback(() => canvasRef.current?.resetView(), []);
  const imageDimsRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const prevTaskIdRef = useRef<number | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  // Mirrors the canvas. A Robopipe Vision turn takes 20-90 seconds, during
  // which the labeler can keep drawing: the boxes a turn replaces have to be
  // read when it lands, not from the closure the run started in — and the
  // task-detail sync reads both without re-running on every edit.
  // useLayoutEffect rather than a bare assignment, matching useLabelShortcuts.
  const annotationsRef = useRef(annotations);
  const isDirtyRef = useRef(isDirty);
  useLayoutEffect(() => {
    annotationsRef.current = annotations;
    isDirtyRef.current = isDirty;
  });
  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<
    Set<string>
  >(() => new Set());
  const [primarySelectedId, setPrimarySelectedId] = useState<string | null>(
    null,
  );
  const clipboardRef = useRef<{
    projectId: number;
    annotations: Annotation[];
  } | null>(null);
  const [hiddenAnnotationIds, setHiddenAnnotationIds] = useState<Set<string>>(
    () => new Set(),
  );
  const isolateAnnotation = useCallback(
    (id: string | null) => {
      setIsolatedLabelId(null);
      setHiddenAnnotationIds(
        id
          ? new Set(annotations.filter((a) => a.id !== id).map((a) => a.id))
          : new Set(),
      );
    },
    [annotations],
  );
  // Transient "h"-hold overlay; does not mutate hiddenAnnotationIds so the
  // per-annotation eye toggles are restored exactly on release.
  const [previewHideAll, setPreviewHideAll] = useState(false);
  const startPreviewHideAll = useCallback(() => setPreviewHideAll(true), []);
  const stopPreviewHideAll = useCallback(() => setPreviewHideAll(false), []);
  // Persistent class filter set by clicking a class in the Annotations tab.
  // Acts radio-style: re-click same class or click "Any" to clear.
  const [isolatedLabelId, setIsolatedLabelId] = useState<string | null>(null);

  const setIsolatedLabel = useCallback(
    (labelId: string) => {
      setIsolatedLabelId(labelId);
      setHiddenAnnotationIds(
        new Set(
          annotations.filter((a) => a.labelId !== labelId).map((a) => a.id),
        ),
      );
      // Only update the drawing label when no region is selected. With an
      // active selection the unanimity effect owns activeLabel and would
      // immediately override this, causing a visible flicker.
      if (selectedAnnotationIds.size === 0) {
        const label = labels.find((l) => String(l.id) === labelId);
        if (label) setActiveLabel(label);
      }
    },
    [labels, selectedAnnotationIds],
  );
  const clearIsolatedLabel = useCallback(
    (showHidden: boolean = false) => {
      setIsolatedLabelId(null);
      if (showHidden) {
        setHiddenAnnotationIds(new Set());
      }
    },
    [setHiddenAnnotationIds],
  );
  const toggleAllAnnotationsVisibility = useCallback(() => {
    if (hiddenAnnotationIds.size === 0) {
      setHiddenAnnotationIds(new Set(annotations.map((a) => a.id)));
    } else {
      setHiddenAnnotationIds(new Set());
      clearIsolatedLabel();
    }
  }, [
    annotations,
    hiddenAnnotationIds,
    setHiddenAnnotationIds,
    clearIsolatedLabel,
  ]);
  const toggleAnnotationVisibility = useCallback(
    (id: string) => {
      setHiddenAnnotationIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      if (isolatedLabelId !== annotations.find((a) => a.id === id)?.labelId) {
        clearIsolatedLabel();
      }
    },
    [isolatedLabelId, annotations, clearIsolatedLabel],
  );
  const [activeLabel, setActiveLabel] = useState<Label | null>(null);
  const canvasState = useCanvasState();

  const setAnnotationsAndDirty: typeof setAnnotations = useCallback((value) => {
    setAnnotations(value);
    setIsDirty(true);
  }, []);

  const history = useHistory({
    annotations,
    setAnnotations: setAnnotationsAndDirty,
  });

  const handleSelect = useCallback(
    (id: string | null, opts?: { additive?: boolean; range?: boolean }) => {
      if (id === null) {
        setSelectedAnnotationIds(new Set());
        setPrimarySelectedId(null);
        return;
      }
      if (opts?.range && primarySelectedId !== null) {
        const ids = annotations.map((a) => a.id);
        const fromIdx = ids.indexOf(primarySelectedId);
        const toIdx = ids.indexOf(id);
        if (fromIdx !== -1 && toIdx !== -1) {
          const [lo, hi] =
            fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
          setSelectedAnnotationIds(new Set(ids.slice(lo, hi + 1)));
          setPrimarySelectedId(id);
          return;
        }
      }
      if (!opts?.additive) {
        setSelectedAnnotationIds(new Set([id]));
        setPrimarySelectedId(id);
        return;
      }
      const next = new Set(selectedAnnotationIds);
      if (next.has(id)) {
        next.delete(id);
        setSelectedAnnotationIds(next);
        setPrimarySelectedId(next.size > 0 ? Array.from(next).pop()! : null);
      } else {
        next.add(id);
        setSelectedAnnotationIds(next);
        setPrimarySelectedId(id);
      }
    },
    [selectedAnnotationIds, primarySelectedId, annotations],
  );

  const handleSelectFromSidebar = useCallback(
    (id: string, opts?: { additive?: boolean; range?: boolean }) => {
      if (isolatedLabelId !== null) {
        const clickedIdx = annotations.findIndex((a) => a.id === id);
        if (clickedIdx >= 0) {
          const isRemoval = !!opts?.additive && selectedAnnotationIds.has(id);
          let added: Annotation[] = [];
          if (!isRemoval) {
            if (opts?.range && primarySelectedId) {
              const anchorIdx = annotations.findIndex(
                (a) => a.id === primarySelectedId,
              );
              if (anchorIdx >= 0) {
                const [lo, hi] =
                  anchorIdx < clickedIdx
                    ? [anchorIdx, clickedIdx]
                    : [clickedIdx, anchorIdx];
                added = annotations.slice(lo, hi + 1);
              } else {
                added = [annotations[clickedIdx]];
              }
            } else {
              added = [annotations[clickedIdx]];
            }
          }
          if (added.some((a) => a.labelId !== isolatedLabelId)) {
            setIsolatedLabelId(null);
            setHiddenAnnotationIds(new Set());
          }
        }
      }
      handleSelect(id, opts);
    },
    [
      isolatedLabelId,
      annotations,
      primarySelectedId,
      selectedAnnotationIds,
      handleSelect,
      setHiddenAnnotationIds,
    ],
  );

  // Wraps history.deleteAnnotation so the id is also removed from the
  // selection Set (the hook itself is now selection-agnostic).
  const deleteAnnotationWithSelection = useCallback(
    (id: string) => {
      history.deleteAnnotation(id);
      setSelectedAnnotationIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setPrimarySelectedId((prev) => (prev === id ? null : prev));
    },
    [history],
  );

  // Set default active label when labels load
  useEffect(() => {
    if (labels.length > 0 && !activeLabel) {
      setActiveLabel(labels[0]);
    }
  }, [labels, activeLabel]);

  // When the selection changes to a unanimous set of same-label regions,
  // mirror that label into activeLabel so the chip reflects the selection.
  // Mixed or empty selections leave activeLabel alone (Q9: indeterminate is display-only).
  useEffect(() => {
    if (selectedAnnotationIds.size === 0) return;
    const selectedLabelIds = new Set(
      annotations
        .filter((a) => selectedAnnotationIds.has(a.id))
        .map((a) => a.labelId),
    );
    if (selectedLabelIds.size !== 1) return;
    const [onlyId] = selectedLabelIds;
    const label = labels.find((l) => String(l.id) === onlyId);
    if (label && label.id !== activeLabel?.id) {
      setActiveLabel(label);
    }
  }, [selectedAnnotationIds, annotations, labels, activeLabel?.id]);

  // Derives the chip's displayed label id. When a multi-selection has mixed
  // labels, no chip is shown (null) while activeLabel stays unchanged for drawing.
  const chipDisplayedLabelId = useMemo<number | null>(() => {
    if (selectedAnnotationIds.size > 1) {
      const labelIds = new Set(
        annotations
          .filter((a) => selectedAnnotationIds.has(a.id))
          .map((a) => a.labelId),
      );
      if (labelIds.size > 1) return null;
    }
    return activeLabel?.id ?? null;
  }, [activeLabel, selectedAnnotationIds, annotations]);

  /**
   * Sync annotations from task detail.
   *
   * A different image, or a refetch with nothing local to lose, replaces the
   * canvas wholesale. A refetch of the SAME image while the canvas holds what
   * the server does not — unsaved edits, or a run's `pred-` boxes — only swaps
   * the staged shapes for the server's. Every finished run invalidates task
   * detail, and a wholesale reset then would throw away whatever the labeler
   * drew while it ran, their undo history, and the ids the next feedback turn
   * replaces.
   */
  const syncedDetailRef = useRef<TaskDetail | null>(null);
  useEffect(() => {
    if (!taskDetail) return;
    const previous = syncedDetailRef.current;
    syncedDetailRef.current = taskDetail;
    const local = annotationsRef.current;
    const hasPredicted = local.some(isPredictedAnnotation);
    if (
      previous?.id === taskDetail.id &&
      (isDirtyRef.current || hasPredicted)
    ) {
      // Nothing staged moved: the canvas is already ahead of this response.
      // RTK Query's structural sharing keeps an unchanged array the same one.
      if (!hasPredicted && previous.autoAnnotations === taskDetail.autoAnnotations)
        return;
      const ids = mapPredictedToStaged(local, taskDetail.autoAnnotations);
      const staged = taskDetailToAnnotations(taskDetail).filter(isStagedAnnotation);
      const kept = local.filter((a) => !isStagedAnnotation(a));
      const present = new Set([...kept, ...staged].map((a) => a.id));
      const rename = (id: string) => ids.get(id) ?? id;
      const renameAll = (prev: Set<string>) =>
        new Set([...prev].map(rename).filter((id) => present.has(id)));
      setAnnotations((prev) => [
        ...prev.filter((a) => !isStagedAnnotation(a)),
        ...staged,
      ]);
      history.remapIds(ids);
      setSelectedAnnotationIds(renameAll);
      setHiddenAnnotationIds(renameAll);
      setPrimarySelectedId((prev) => {
        const id = prev === null ? null : rename(prev);
        return id !== null && present.has(id) ? id : null;
      });
      setVisionSession((prev) =>
        prev !== null && prev.taskId === taskDetail.id
          ? { ...prev, addedIds: prev.addedIds.map(rename) }
          : prev,
      );
      return;
    }
    setAnnotations(taskDetailToAnnotations(taskDetail));
    setIsDirty(false);
    setHiddenAnnotationIds(new Set());
    setPreviewHideAll(false);
    history.reset();
    setSelectedAnnotationIds(new Set());
    setPrimarySelectedId(null);
    setSelectedInferredId(null);
    imageDimsRef.current = { width: 0, height: 0 };
    if (prevTaskIdRef.current !== taskDetail.id) {
      setIsolatedLabelId(null);
      prevTaskIdRef.current = taskDetail.id;
    }
  }, [taskDetail]);

  const handleReorderAnnotations = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      setAnnotationsAndDirty((prev) => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
    },
    [setAnnotationsAndDirty],
  );

  const canGroup = useMemo(
    () => canGroupSelection(annotations, selectedAnnotationIds),
    [annotations, selectedAnnotationIds],
  );

  const handleGroupSelected = useCallback(() => {
    if (!canGroup) return;
    const after = groupSelected(annotations, selectedAnnotationIds);
    // Apply the full reordered array in one shot (groupSelected moves members
    // to be contiguous — updateAnnotation can't express a reorder).
    setAnnotationsAndDirty(after);
    // Record for undo: only the annotations whose groupId changed.
    const changes = annotations
      .map((orig) => {
        const updated = after.find((a) => a.id === orig.id);
        if (!updated || updated.groupId === orig.groupId) return null;
        return { before: orig, after: updated };
      })
      .filter(Boolean) as { before: Annotation; after: Annotation }[];
    if (changes.length > 0) history.pushBatchEntry({ label: "group", changes });
  }, [annotations, selectedAnnotationIds, canGroup, history, setAnnotationsAndDirty]);

  const handleUngroupAnnotations = useCallback(
    (groupIds: Set<string>) => {
      const before = annotations.filter(
        (a) => a.groupId && groupIds.has(a.groupId),
      );
      history.runBatch("ungroup", () => {
        for (const a of before) {
          history.updateAnnotation(a.id, { groupId: null });
        }
        // auto-dissolve any remaining singletons (silently clears their groupId)
        // This runs implicitly via autoDissolve in the utilities — we replicate
        // it here for annotations that are NOT in the explicitly ungrouped set
        // but might become singletons after this batch.
        const afterUngroup = ungroupAnnotations(annotations, groupIds);
        for (const a of annotations) {
          if (a.groupId && !groupIds.has(a.groupId)) {
            const afterA = afterUngroup.find((x) => x.id === a.id);
            if (afterA && afterA.groupId !== a.groupId) {
              history.updateAnnotation(a.id, { groupId: afterA.groupId });
            }
          }
        }
      });
    },
    [annotations, history],
  );

  const handleMoveAnnotationInSidebar = useCallback(
    (fromIndex: number, toIndex: number, targetGroupId: string | null) => {
      const movedAnnotation = annotations[fromIndex];
      if (!movedAnnotation) return;

      if (targetGroupId) {
        // Joining a group — addToGroup handles the groupId assignment,
        // compaction (makeGroupContiguous), and dissolving the old group.
        const groupMember = annotations.find((a) => a.groupId === targetGroupId);
        if (groupMember && movedAnnotation.labelId !== groupMember.labelId) {
          toast.error(t("page.groupLabelMismatch"));
          return;
        }
        if (movedAnnotation.groupId === targetGroupId) {
          handleReorderAnnotations(fromIndex, toIndex);
          return;
        }
        const after = addToGroup(annotations, movedAnnotation.id, targetGroupId);
        const changes = annotations
          .map((orig) => {
            const updated = after.find((a) => a.id === orig.id);
            if (!updated || updated.groupId === orig.groupId) return null;
            return { before: orig, after: updated };
          })
          .filter(Boolean) as { before: Annotation; after: Annotation }[];
        setAnnotationsAndDirty(after);
        if (changes.length > 0) history.pushBatchEntry({ label: "group", changes });
      } else if (movedAnnotation.groupId) {
        // Leaving a group — removeFromGroup handles the groupId clear,
        // compaction of the remaining members, and auto-dissolve.
        const after = removeFromGroup(annotations, movedAnnotation.id);
        const changes = annotations
          .map((orig) => {
            const updated = after.find((a) => a.id === orig.id);
            if (!updated || updated.groupId === orig.groupId) return null;
            return { before: orig, after: updated };
          })
          .filter(Boolean) as { before: Annotation; after: Annotation }[];
        setAnnotationsAndDirty(after);
        if (changes.length > 0) history.pushBatchEntry({ label: "ungroup", changes });
      } else {
        handleReorderAnnotations(fromIndex, toIndex);
      }
    },
    [annotations, history, handleReorderAnnotations, setAnnotationsAndDirty, t],
  );

  const handleClear = useCallback(() => {
    if (selectedAnnotationIds.size === 0) return;
    const ids = Array.from(selectedAnnotationIds);
    history.runBatch("delete", () => {
      for (const id of ids) history.deleteAnnotation(id);
    });
    setSelectedAnnotationIds(new Set());
    setPrimarySelectedId(null);
  }, [selectedAnnotationIds, history]);

  const cloneAnnotation = useCallback(
    (a: Annotation): Annotation => ({
      ...a,
      bbox: a.bbox ? { ...a.bbox } : undefined,
      points: a.points?.map(([x, y]) => [x, y] as [number, number]),
    }),
    [],
  );

  const handleCopySelection = useCallback(() => {
    if (!projectId || selectedAnnotationIds.size === 0) return;
    const selected = annotations.filter((a) => selectedAnnotationIds.has(a.id));
    if (selected.length === 0) return;
    clipboardRef.current = {
      projectId,
      annotations: selected.map(cloneAnnotation),
    };
    toast.success(t("page.copiedRegions", { count: selected.length }));
  }, [annotations, selectedAnnotationIds, projectId, cloneAnnotation, t]);

  const handleGroupTranslate = useCallback(
    (updates: Array<{ id: string; updates: Partial<Annotation> }>) => {
      if (updates.length === 0) return;
      history.runBatch("move", () => {
        for (const { id, updates: u } of updates)
          history.updateAnnotation(id, u);
      });
    },
    [history],
  );

  const handlePasteClipboard = useCallback(() => {
    if (!projectId) return;
    const clip = clipboardRef.current;
    if (!clip || clip.annotations.length === 0) {
      toast.info(t("page.clipboardEmpty"));
      return;
    }
    if (clip.projectId !== projectId) {
      toast.error(t("page.clipboardOtherProject"));
      return;
    }
    const stamp = Date.now();
    const rawPasted: Annotation[] = clip.annotations.map((a, idx) => {
      const cloned = cloneAnnotation(a);
      return { ...cloned, id: `ann-${stamp}-${idx}`, apiId: undefined };
    });
    // Preserve group structure: build a map from original id to new pasted annotation
    // so assignPasteGroups can find the source groupIds
    const sourceMap = clip.annotations.map((original, idx) => ({
      ...rawPasted[idx],
      // carry original id temporarily so assignPasteGroups can look up the groupId
      _originalId: original.id,
    }));
    const pastedWithSrcIds = rawPasted.map((a, idx) => ({
      ...a,
      id: sourceMap[idx]._originalId,
    }));
    const withGroups = assignPasteGroups(pastedWithSrcIds, clip.annotations);
    // Re-assign the new pasted ids
    const pasted: Annotation[] = rawPasted.map((a, idx) => ({
      ...a,
      groupId: withGroups[idx]?.groupId,
    }));
    history.runBatch("paste", () => {
      for (const ann of pasted) history.addAnnotation(ann);
    });
    const newIds = pasted.map((a) => a.id);
    setSelectedAnnotationIds(new Set(newIds));
    setPrimarySelectedId(newIds[newIds.length - 1] ?? null);
    toast.success(t("page.pastedRegions", { count: pasted.length }));
  }, [projectId, history, cloneAnnotation, t]);

  const handleSelectLabel = useCallback(
    (labelId: number) => {
      const label = labels.find((l) => l.id === labelId);
      if (!label) return;

      const changed = label.id !== activeLabel?.id;

      if (selectedAnnotationIds.size > 0) {
        const ids = Array.from(selectedAnnotationIds);
        const newLabelId = String(label.id);
        history.runBatch("relabel", () => {
          for (const id of ids) {
            const a = annotations.find((x) => x.id === id);
            history.updateAnnotation(id, {
              labelId: newLabelId,
              labelName: label.name,
              color: label.color,
            });
            // Auto-eject from group if label changes while in a group
            if (a?.groupId && a.labelId !== newLabelId) {
              const groupSiblings = annotations.filter(
                (x) => x.id !== id && x.groupId === a.groupId,
              );
              history.updateAnnotation(id, { groupId: null });
              // Auto-dissolve the group if it becomes a singleton
              if (groupSiblings.length === 1) {
                history.updateAnnotation(groupSiblings[0].id, { groupId: null });
              }
            }
          }
        });
        // Notify user if any grouped regions were ejected
        const ejectedCount = ids.filter((id) => {
          const a = annotations.find((x) => x.id === id);
          return a?.groupId && a.labelId !== newLabelId;
        }).length;
        if (ejectedCount > 0) {
          toast.info(t("page.ejectedFromGroup", { count: ejectedCount }));
        }
      }

      setActiveLabel(label);

      if (changed && isolatedLabelId !== null) {
        setIsolatedLabelId(null);
      }
    },
    [labels, activeLabel?.id, selectedAnnotationIds, annotations, history, isolatedLabelId, t],
  );

  // Creates a label inline from the class toolbar. Sets the newly created
  // label directly as active (rather than routing through handleSelectLabel,
  // which looks the id up in `labels` — the refetch triggered by the create
  // mutation's cache invalidation hasn't landed yet, so the lookup would
  // no-op). The default-selection effect above only fires when activeLabel
  // is null, so this won't get clobbered once the refetched list arrives.
  const handleCreateLabel = useCallback(
    async ({ name, color, description, renameArchived }: LabelEditorInput) => {
      if (!projectId) {
        throw new Error("Project is not loaded yet");
      }
      const created = await createProjectLabel({
        projectId,
        name,
        color,
        description: description ?? null,
        ...(renameArchived && { renameArchived }),
      }).unwrap();
      setActiveLabel(created);
    },
    [projectId, createProjectLabel],
  );

  // Edits a label inline from the class toolbar. Annotations denormalise the
  // label's name and colour, so the loaded ones are patched in place with the
  // plain setter: nothing about the task changed, so it must not go dirty.
  // Undo/redo snapshots keep the pre-edit colour; they are cosmetic and
  // refresh on the next task load.
  const handleUpdateLabel = useCallback(
    async (
      labelId: number,
      { name, color, description, renameArchived }: LabelEditorInput,
    ) => {
      if (!projectId) {
        throw new Error("Project is not loaded yet");
      }
      const updated = await updateProjectLabel({
        projectId,
        labelId,
        name,
        color,
        description: description ?? null,
        ...(renameArchived && { renameArchived }),
      }).unwrap();
      const key = String(labelId);
      setAnnotations((prev) =>
        prev.map((a) =>
          a.labelId === key
            ? { ...a, labelName: updated.name, color: updated.color }
            : a,
        ),
      );
      setActiveLabel((prev) => (prev?.id === labelId ? updated : prev));
    },
    [projectId, updateProjectLabel],
  );

  // Robopipe Vision writes a label description from an example of it: the
  // selected or largest region of that label on this image (unsaved ones
  // too), else the server's pick. The text comes back as a draft for the
  // field that asked; nothing is saved here.
  const [suggestLabelDescription] = useSuggestLabelDescriptionMutation();
  const handleGenerateDescription = useCallback<GenerateLabelDescription>(
    async ({ labelId, name, currentDescription }) => {
      if (!projectId) {
        throw new Error("Project is not loaded yet");
      }
      const taskId = selectedTaskId ?? undefined;
      const region =
        labelId !== undefined && taskId !== undefined
          ? pickExampleRegion(
              annotationsRef.current,
              String(labelId),
              primarySelectedId,
            )
          : undefined;
      const { description } = await suggestLabelDescription({
        projectId,
        body: {
          labelId,
          name,
          currentDescription: currentDescription.trim() || undefined,
          taskId,
          region,
        },
      }).unwrap();
      return description;
    },
    [projectId, selectedTaskId, primarySelectedId, suggestLabelDescription],
  );

  // Archives a label from the class toolbar. The server stamps it and
  // recounts the images that carried its regions; here the open image drops
  // them with the plain setter (they were saved, and the editor refuses to
  // archive while there are unsaved edits, so nothing about the task changed
  // from the labeller's side) and the undo history forgets them: undoing into
  // an archived label would be rejected on save. The replacement active label
  // is picked from the current list rather than left to the default-selection
  // effect, which would re-pick the archived one from the stale cache.
  const handleArchiveLabel = useCallback(
    async (labelId: number) => {
      if (!projectId) {
        throw new Error("Project is not loaded yet");
      }
      await archiveProjectLabel({ projectId, labelId }).unwrap();
      const key = String(labelId);
      const removed = new Set(
        annotationsRef.current.filter((a) => a.labelId === key).map((a) => a.id),
      );
      if (removed.size > 0) {
        setAnnotations((prev) => prev.filter((a) => a.labelId !== key));
        setSelectedAnnotationIds((prev) => {
          const next = new Set([...prev].filter((id) => !removed.has(id)));
          return next.size === prev.size ? prev : next;
        });
        setPrimarySelectedId((prev) =>
          prev !== null && removed.has(prev) ? null : prev,
        );
      }
      history.reset();
      setActiveLabel((prev) =>
        prev?.id === labelId
          ? (labels.find((l) => l.id !== labelId) ?? null)
          : prev,
      );
      if (isolatedLabelId === key) {
        clearIsolatedLabel();
      }
    },
    [
      projectId,
      archiveProjectLabel,
      history,
      labels,
      isolatedLabelId,
      clearIsolatedLabel,
    ],
  );

  // Brings an archived label and its regions back. The open image reloads from
  // the server through the invalidated task cache; the toolbar refuses while
  // it has unsaved edits, so that reload cannot clobber anything.
  const handleRestoreLabel = useCallback(
    async (labelId: number) => {
      if (!projectId) {
        throw new Error("Project is not loaded yet");
      }
      await restoreProjectLabel({ projectId, labelId }).unwrap();
    },
    [projectId, restoreProjectLabel],
  );

  // A label in the URL filter that is no longer active (archived in another
  // tab, a stale link, or just now) would silently filter on a chip nobody can
  // see or clear, so it is dropped as soon as the label list says so.
  useEffect(() => {
    if (isLoadingLabels) return;
    const stale = filter.labelIds.filter(
      (id) => !labels.some((l) => l.id === id),
    );
    if (stale.length === 0) return;
    setSortAndFilter({
      sort,
      filter: {
        ...filter,
        labelIds: filter.labelIds.filter((id) => !stale.includes(id)),
      },
    });
  }, [isLoadingLabels, labels, filter, sort, setSortAndFilter]);

  // ── Robopipe Vision conversation ───────────────────────────────────────────
  // One image, one live conversation, and it outlives everything the labeler
  // does short of starting another one: the run panel, the save, a cancelled
  // turn, and above all the image itself. Leaving a half-corrected image to go
  // and do something else, then coming back to finish the thread, is the point
  // — so the only thing that ends a conversation is a fresh Annotate, which
  // the API closes server-side as it opens the next one.
  // The labeler's own turns stream; the batch fan-out keeps the plain
  // `runVisionDetection` mutation (see useBatchAutoAnnotate).
  const { runDetection: runVisionStream, sendFeedback: sendVisionFeedbackStream } =
    useVisionStream();
  const [getVisionTranscript] = useLazyGetVisionTranscriptQuery();
  const [deleteVisionNote] = useDeleteVisionNoteMutation();
  const [updateVisionNote] = useUpdateVisionNoteMutation();
  const [saveVisionTurnNote] = useSaveVisionTurnNoteMutation();
  const appDispatch = useAppDispatch();
  const [visionSession, setVisionSession] = useState<VisionSession | null>(
    null,
  );
  // The project note the last feedback turn was saved as, for the result
  // card's "Saved to project notes · Edit · Exclude" or "Not saved · Save".
  // Cleared whenever the card's run is replaced: the note stays on the
  // project, only the card's offer expires.
  const [visionSavedNote, setVisionSavedNote] =
    useState<VisionSavedNote | null>(null);
  /** Why the last Edit, Exclude or Save did not go through, if it did not. */
  const [visionNoteError, setVisionNoteError] = useState<string | null>(null);
  /** An Exclude or Save is in flight; the chips wait for it. */
  const [visionNoteBusy, setVisionNoteBusy] = useState(false);
  /**
   * The turn ledger of the conversation on screen, or null when the labeler
   * has not asked for it.
   *
   * Null is "not open", not "empty": the panel shows the transcript only once
   * somebody clicks Feedback, and this is what that click fetches. Kept out of
   * the task detail response deliberately — that one is fetched for every
   * image the labeler lands on, this one only for the ones they resume.
   */
  const [visionTranscript, setVisionTranscript] = useState<
    VisionTranscriptTurn[] | null
  >(null);
  const [visionTranscriptLoading, setVisionTranscriptLoading] = useState(false);
  const visionTranscriptRef = useRef(visionTranscript);
  useLayoutEffect(() => {
    visionTranscriptRef.current = visionTranscript;
  });
  // Mirrors the state so the handlers below can read the CURRENT session
  // without taking it as a dependency.
  const visionSessionRef = useRef(visionSession);
  useLayoutEffect(() => {
    visionSessionRef.current = visionSession;
  });
  const visionSessionForTask =
    visionSession !== null && visionSession.taskId === selectedTaskId
      ? visionSession
      : null;

  /**
   * Fetch the ledger of the conversation on this image.
   *
   * Best effort: a transcript that will not load must not stop the labeler
   * giving feedback, which is the thing they actually came for — the panel
   * simply shows the feedback box without the history above it.
   */
  const loadVisionTranscript = useCallback(async () => {
    const session = visionSessionRef.current;
    if (!projectId || session === null) return;
    setVisionTranscriptLoading(true);
    try {
      const result = await getVisionTranscript({
        projectId,
        taskId: session.taskId,
        conversationId: session.conversationId,
      }).unwrap();
      // The image may have changed while this was in flight.
      if (visionSessionRef.current?.conversationId !== session.conversationId)
        return;
      setVisionTranscript(result.turns);
    } catch {
      setVisionTranscript([]);
    } finally {
      setVisionTranscriptLoading(false);
    }
  }, [projectId, getVisionTranscript]);

  const [isSaving, setIsSaving] = useState(false);
  /**
   * Save the image. A plain save never approves: staged auto-annotations stay
   * awaiting review, with the labeller's edits to them written back onto the
   * staged rows. `approve` promotes them into real annotations as well.
   */
  const handleSave = useCallback(
    async (options?: { reviewed?: boolean; approve?: boolean }) => {
      if (!projectId || selectedTaskId === null) return;
      setIsSaving(true);
      try {
        const body: UpdateTask = options?.approve
          ? { ...annotationsToUpdatePayload(annotations), approve: true }
          : (() => {
              const { saved, stagedAnnotations } =
                splitStagedAnnotations(annotations);
              return {
                ...annotationsToUpdatePayload(saved),
                ...(stagedAnnotations && { stagedAnnotations }),
              };
            })();
        if (options?.reviewed) body.reviewed = true;
        await updateTask({
          projectId,
          taskId: selectedTaskId,
          body,
        }).unwrap();
        setIsDirty(false);
        // The conversation deliberately survives the save. Saving means these
        // boxes are good, not that the image is finished — a labeler who spots
        // a miss right afterwards should still be able to say so rather than
        // find Feedback greyed out with no explanation. It ends only when a
        // fresh Annotate opens the next one.
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, selectedTaskId, annotations, updateTask],
  );

  const handleSaveEmpty = useCallback(() => {
    handleSave({ reviewed: true });
  }, [handleSave]);

  const handleApprove = useCallback(
    () => handleSave({ approve: true }),
    [handleSave],
  );

  /**
   * Every single-image auto-annotation this page knows about, by task.
   *
   * Per task because a run belongs to the image it was started on, not to the
   * page: the labeler may start one, arrow to the next image and start another
   * there, and coming back to the first must find its card — live, finished,
   * or recovered from the server after a reload — where they left it. Only the
   * open task's entry is ever rendered, and only it drives ✨'s busy state.
   */
  const [runs, setRuns] = useState<ReadonlyMap<number, TaskRun>>(
    () => new Map(),
  );
  /**
   * The request behind each live run, so cancelling or leaving the page can
   * abort it, and a settled request can tell whether it is still the current
   * one before writing anything. Not RTK Query's isLoading: that cannot say
   * WHICH task a request belongs to. `runId` comes from one page-wide counter.
   */
  const runRequestsRef = useRef(new Map<number, RunRequest>());
  const runIdRef = useRef(0);
  /**
   * When this page last stopped following a run, per task — it settled, or the
   * labeler cancelled it. Cancelling only stops this page reading: the server
   * finishes the turn and holds its lock meanwhile. And a task-detail refetch
   * that left before a run settled can land after it, still carrying the lock.
   * Either way the recovery effect must not bring that run back as "running".
   */
  const releasedAtRef = useRef(new Map<number, number>());
  const isCurrentRun = useCallback(
    (taskId: number, runId: number) =>
      runRequestsRef.current.get(taskId)?.runId === runId,
    [],
  );
  /** Claim a new run on `taskId`; the caller fills in `abort` once it has one. */
  const claimRun = useCallback((taskId: number): RunRequest => {
    const request: RunRequest = { runId: ++runIdRef.current, abort: () => {} };
    runRequestsRef.current.set(taskId, request);
    return request;
  }, []);
  /** The run is over: forget its request, unless a newer one replaced it. */
  const releaseRun = useCallback(
    (taskId: number, runId: number) => {
      if (!isCurrentRun(taskId, runId)) return;
      runRequestsRef.current.delete(taskId);
      releasedAtRef.current.set(taskId, Date.now());
    },
    [isCurrentRun],
  );
  // Mirrors the live selection so a settled auto-annotation can tell whether
  // the image it was requested for is the one on screen.
  const selectedTaskIdRef = useRef(selectedTaskId);
  useLayoutEffect(() => {
    selectedTaskIdRef.current = selectedTaskId;
  });

  // How the latest auto-annotation of each task ended, for the banner at the
  // top of the settings dialog. Only the newest run is kept, and only for
  // the task it ran on: switching images drops it from view.
  const [lastRun, setLastRun] = useState<{
    taskId: number;
    outcome: PreAnnotateRunOutcome;
  } | null>(null);
  const lastRunForTask =
    lastRun !== null && lastRun.taskId === selectedTaskId
      ? lastRun.outcome
      : null;

  /** Rewrite one task's entry; returning null or undefined drops it. */
  const updateRun = useCallback(
    (
      taskId: number,
      update: (prev: TaskRun | undefined) => TaskRun | null | undefined,
    ) => {
      setRuns((prev) => {
        const current = prev.get(taskId);
        const next = update(current);
        if (next === current) return prev;
        const map = new Map(prev);
        if (next == null) map.delete(taskId);
        else map.set(taskId, next);
        return map;
      });
    },
    [],
  );
  /** Put `view` on `taskId`'s card. Any new view drops the previews. */
  const showRun = useCallback(
    (taskId: number, view: PreAnnotateRunView) => {
      updateRun(taskId, () => ({ view, previewBoxes: [] }));
      // Every new run retires the previous turn's saved-note chip: it belongs
      // to the card that reported that turn. A feedback turn sets it again.
      if (view.kind === "running" && selectedTaskIdRef.current === taskId) {
        setVisionSavedNote(null);
        setVisionNoteError(null);
      }
    },
    [updateRun],
  );
  /**
   * Patch a live run card — phase, thought, box count — without replacing it.
   *
   * Separate from `showRun` because that one is a transition: it drops the
   * previews and would reset the elapsed clock's `startedAt` on every delta.
   * A streamed turn fires dozens of these a second, and none of them is a new
   * run.
   */
  const updateRunning = useCallback(
    (
      taskId: number,
      patch: Partial<Extract<PreAnnotateRunView, { kind: "running" }>>,
      previewBox?: PreviewBoxSpec,
    ) => {
      updateRun(taskId, (prev) =>
        prev !== undefined && prev.view.kind === "running"
          ? {
              ...prev,
              view: { ...prev.view, ...patch },
              previewBoxes: previewBox
                ? [...prev.previewBoxes, previewBox]
                : prev.previewBoxes,
            }
          : prev,
      );
    },
    [updateRun],
  );
  /** Close a card, but only if it still shows the view the caller saw. */
  const dismissRunPanel = useCallback((view: PreAnnotateRunView) => {
    setRuns((prev) => {
      for (const [taskId, run] of prev) {
        if (run.view !== view) continue;
        const map = new Map(prev);
        map.delete(taskId);
        return map;
      }
      return prev;
    });
  }, []);
  /**
   * A run ended: keep it for the settings banner and show the result card.
   * On the run's own task, whether or not it is open — one that ended while the
   * labeler was elsewhere waits there for them.
   */
  const reportOutcome = useCallback(
    (taskId: number, outcome: PreAnnotateRunOutcome, note?: string) => {
      setLastRun({ taskId, outcome });
      showRun(taskId, { kind: "outcome", outcome, note });
    },
    [showRun],
  );
  const openRun =
    selectedTaskId !== null ? runs.get(selectedTaskId) : undefined;
  const runView = openRun?.view ?? null;
  const previewBoxes = openRun?.previewBoxes ?? NO_PREVIEW_BOXES;
  /** A run — streamed here or recovered from the server — is live on the open image. */
  const isPredicting = runView?.kind === "running";
  const isDetachedRunOpen = isPredicting && openRun?.detached !== undefined;
  /** Images this tab has a single-image run in flight on, for the Data source chips. */
  const runningTaskIds = useMemo(
    () =>
      new Set(
        [...runs].filter(([, run]) => run.view.kind === "running").map(([id]) => id),
      ),
    [runs],
  );

  /**
   * Resume this image's conversation: show what was already said, and open the
   * feedback box.
   *
   * No Vision model call — the labeler has not written anything yet. This only
   * puts the panel back on screen in the state they left it in, which is the
   * whole point of a conversation that outlives the page that started it.
   */
  const handleResumeVision = useCallback(() => {
    const session = visionSessionRef.current;
    if (session === null || session.taskId !== selectedTaskId) return;
    setVisionTranscript([]);
    void loadVisionTranscript();
    showRun(session.taskId, { kind: "resumed", turn: session.turn });
  }, [selectedTaskId, loadVisionTranscript, showRun]);

  /**
   * Predicted boxes as unsaved `pred-` bbox annotations. Predictions for a
   * label that no longer exists are skipped, so the result can be shorter
   * than the input.
   */
  const buildPredictedRectangles = useCallback(
    (rectangles: PredictedRectangle[], stamp: number): Annotation[] => {
      const labelById = new Map(labels.map((l) => [l.id, l]));
      return rectangles.flatMap((r, idx): Annotation[] => {
        const label = labelById.get(r.labelId);
        if (!label) return [];
        return [
          {
            id: `pred-${stamp}-${idx}`,
            apiId: undefined,
            labelId: String(label.id),
            labelName: label.name,
            color: label.color,
            type: "bbox" as const,
            bbox: { x: r.x, y: r.y, width: r.width, height: r.height },
          },
        ];
      });
    },
    [labels],
  );

  /**
   * A Robopipe Vision turn's boxes.
   *
   * Replaces only `replaceIds` — what the PREVIOUS turn added, or the staged
   * set a fresh run supersedes — so boxes the labeler drew or corrected by
   * hand survive a feedback turn; ids they deleted meanwhile simply do not
   * match. Returns the ids it placed, which is what the next turn replaces.
   *
   * Not dirty: the run staged these server-side already, so leaving the image
   * loses nothing — the same reason staged boxes loaded from task detail are
   * not dirty either.
   *
   * Recorded as one undo step rather than clearing the stack. That used to be
   * right: a run replaced every box of its geometry on a canvas that was
   * guaranteed empty, so there was nothing coherent to undo back to. A
   * feedback turn now lands on top of whatever the labeler did since the last
   * one, which makes the whole turn exactly "these boxes arrived, those ones
   * left" — and Ctrl+Z is the obvious way back from a pass you did not want.
   */
  const placeVisionRectangles = useCallback(
    (
      rectangles: PredictedRectangle[],
      stamp: number,
      replaceIds: ReadonlySet<string>,
    ): string[] => {
      const predicted = buildPredictedRectangles(rectangles, stamp);
      // Read from the mirror, not from a closure: the labeler may have drawn,
      // edited or deleted boxes while this turn was in flight, and the undo
      // entry has to restore what was actually on screen.
      const removed = annotationsRef.current.filter((a) => replaceIds.has(a.id));
      setAnnotations((prev) => [
        ...prev.filter((a) => !replaceIds.has(a.id)),
        ...predicted,
      ]);
      history.pushReplaceEntry({
        label: "preannotate",
        removed,
        added: predicted,
      });
      setSelectedAnnotationIds(new Set());
      setPrimarySelectedId(null);
      return predicted.map((a) => a.id);
    },
    [buildPredictedRectangles, history.pushReplaceEntry],
  );

  /**
   * The preview half of a streamed turn: everything before `done`.
   *
   * Every callback is guarded on the run id, and writes into the run's own
   * entry whether or not its image is open: previews only ever render on their
   * own image, and a labeler who comes back mid-run finds every box streamed
   * while they were away. `done` is deliberately NOT handled here: it is the
   * authoritative result and each caller does with it exactly what it did with
   * the mutation's resolved value.
   */
  const previewCallbacks = useCallback(
    (taskId: number, runId: number) => {
      const labelById = new Map(labels.map((l) => [l.id, l]));
      let thought = "";
      let boxCount = 0;
      const live = () => isCurrentRun(taskId, runId);

      return {
        onPhase: (phase: VisionStreamPhaseEnum) => {
          if (live()) updateRunning(taskId, { phase });
        },
        onThought: (text: string) => {
          if (!live()) return;
          thought += text;
          const headline = thoughtHeadline(thought);
          if (headline !== null) updateRunning(taskId, { thought: headline });
        },
        onBox: (rectangle: PredictedRectangle) => {
          if (!live()) return;
          // Same rule as buildPredictedRectangles: a box for a label that no
          // longer exists has no colour to draw in and no home to land in.
          const label = labelById.get(rectangle.labelId);
          if (!label) return;
          boxCount += 1;
          updateRunning(
            taskId,
            { boxCount },
            {
              key: `stream-${runId}-${boxCount}`,
              color: label.color,
              x: rectangle.x,
              y: rectangle.y,
              width: rectangle.width,
              height: rectangle.height,
            },
          );
        },
      };
    },
    [labels, isCurrentRun, updateRunning],
  );

  /**
   * Drop staged shapes from the selection as a fresh run hides them, so a
   * Delete or a nudge cannot reach a shape the labeler can no longer see.
   */
  const deselectStaged = useCallback(() => {
    setSelectedAnnotationIds((prev) => {
      const next = new Set([...prev].filter((id) => !isStagedId(id)));
      return next.size === prev.size ? prev : next;
    });
    setPrimarySelectedId((prev) => (prev !== null && isStagedId(prev) ? null : prev));
  }, []);

  const startPreAnnotate = useCallback(
    (modelType: PreAnnotateModelTypeEnum) => {
      if (!projectId || selectedTaskId === null) return;

      const isDetection = modelType === PreAnnotateModelTypeEnum.DETECTION;

      // Never replace what the labeler drew: a run only starts on a canvas
      // with none of THEIR shapes of the geometry it would produce. Staged
      // ones are fair game — the run replaces them, and the toolbar asked
      // first. The server checks the same thing against what is saved —
      // handlePreAnnotate saves first when the two disagree.
      const conflictingAnnotations = annotations.filter(
        (a) => a.type === (isDetection ? "bbox" : "polygon") && !isStagedAnnotation(a),
      );
      if (conflictingAnnotations.length > 0) return;

      // Detection only, and only with something to look for. Both are also
      // the ✨ button's disabled reasons, so this is the belt to their braces
      // (the run panel's Run again shares the same gates).
      if (!isDetection) return;
      if (!visionAvailable || !hasUsableDescriptions(labelDescriptions)) return;

      const stamp = Date.now();
      const taskId = selectedTaskId;

      // A fresh run supersedes whatever conversation this image had: the API
      // closes the old row itself, so no DELETE from here.
      visionSessionRef.current = null;
      setVisionSession(null);
      setVisionTranscript(null);
      showRun(taskId, {
        kind: "running",
        modelType,
        startedAt: stamp,
        phase: VisionStreamPhaseEnum.IMAGE,
        replacesStaged: true,
      });
      deselectStaged();

      // Claim this run. Everything below checks it is still current before
      // touching state, so a cancelled or superseded response can never write
      // annotations. The turn streams: the abort handle wraps an
      // AbortController, and the card fills in as the model works rather than
      // only when it finishes.
      const request = claimRun(taskId);
      const { runId } = request;
      const settle = () => releaseRun(taskId, runId);

      request.abort = runVisionStream(
        {
          projectId,
          taskId,
          body: { modelType },
        },
        {
          ...previewCallbacks(taskId, runId),
          onDone: (result) => {
            if (!isCurrentRun(taskId, runId)) return;
            // The labeler moved on. The boxes are staged server-side, so the
            // task detail they come back to brings them; the card waits on
            // this task's entry.
            if (selectedTaskIdRef.current !== taskId) {
              reportOutcome(
                taskId,
                visionTurnOutcome(result.rectangles.length, result.summary, result.turn),
              );
              settle();
              return;
            }
            // The previews were a courtesy; these rectangles are the answer.
            // A first turn replaces every staged shape still on the canvas,
            // of either geometry: the server just replaced the whole staged
            // set. Read now, not at start — they stayed up during the run so
            // a cancel or a failure would leave them untouched.
            const ids = placeVisionRectangles(
              result.rectangles,
              stamp,
              new Set(
                annotationsRef.current.filter(isStagedAnnotation).map((a) => a.id),
              ),
            );
            // Kept even when the model found nothing: "nothing here" is
            // exactly the answer worth arguing with.
            setVisionSession({
              taskId,
              conversationId: result.conversationId,
              turn: result.turn,
              addedIds: ids,
            });
            reportOutcome(
              taskId,
              visionTurnOutcome(ids.length, result.summary, result.turn),
            );
            settle();
          },
          onError: (message) => {
            if (!isCurrentRun(taskId, runId)) return;
            reportOutcome(taskId, { kind: "error", modelType, message });
            settle();
          },
        },
      ).abort;
    },
    [
      projectId,
      selectedTaskId,
      visionAvailable,
      labelDescriptions,
      annotations,
      runVisionStream,
      previewCallbacks,
      claimRun,
      releaseRun,
      isCurrentRun,
      placeVisionRectangles,
      showRun,
      reportOutcome,
      deselectStaged,
      t,
    ],
  );

  /**
   * Start a run, saving the image first when only the save stands in its way.
   *
   * The server refuses a run while the task has SAVED shapes of the run's
   * geometry, so boxes the labeler deleted but did not save still block it
   * there. A canvas with none of them is the labeler's answer: save it, then
   * run. Anything else on the canvas goes along, since a save is the whole
   * image. A failed save starts nothing.
   */
  const handlePreAnnotate = useCallback(
    async (modelType: PreAnnotateModelTypeEnum) => {
      if (selectedTaskId === null || isSaving) return;
      const taskId = selectedTaskId;

      const isDetection = modelType === PreAnnotateModelTypeEnum.DETECTION;
      const saved =
        taskDetail?.id !== taskId
          ? []
          : isDetection
            ? taskDetail.rectangleAnnotations
            : taskDetail.polygonAnnotations;
      // Staged shapes are not the labeler's and do not count either way.
      const onCanvas = annotations.some(
        (a) => a.type === (isDetection ? "bbox" : "polygon") && !isStagedAnnotation(a),
      );

      if ((saved?.length ?? 0) > 0 && !onCanvas) {
        try {
          await handleSave();
        } catch (err) {
          toast.error(apiErrorMessage(err, t("page.saveBeforeRunFailed")));
          return;
        }
        if (selectedTaskIdRef.current !== taskId) return;
      }

      startPreAnnotate(modelType);
    },
    [selectedTaskId, isSaving, taskDetail, annotations, handleSave, startPreAnnotate, t],
  );

  // What a run started now would throw away: every staged shape on the
  // canvas (the server replaces the whole staged set, whatever its geometry)
  // and, for Robopipe Vision, the conversation a fresh run starts over.
  const stagedOnCanvasCount = useMemo(
    () => annotations.filter(isStagedAnnotation).length,
    [annotations],
  );
  const runDiscardsTurns = visionSessionForTask?.turn ?? 0;

  /** The run waiting on the replace dialog, or null while it is closed. */
  const [pendingReplaceRun, setPendingReplaceRun] =
    useState<PreAnnotateModelTypeEnum | null>(null);

  /**
   * Every auto-annotate entry point — the toolbar's ✨ and the run card's Run
   * again. Asks first whenever the run would replace something; otherwise
   * starts straight away.
   */
  const requestPreAnnotate = useCallback(
    (modelType: PreAnnotateModelTypeEnum) => {
      if (stagedOnCanvasCount > 0 || runDiscardsTurns > 0) {
        setPendingReplaceRun(modelType);
        return;
      }
      void handlePreAnnotate(modelType);
    },
    [stagedOnCanvasCount, runDiscardsTurns, handlePreAnnotate],
  );

  const handleConfirmReplaceRun = useCallback(() => {
    if (pendingReplaceRun === null) return;
    setPendingReplaceRun(null);
    void handlePreAnnotate(pendingReplaceRun);
  }, [pendingReplaceRun, handlePreAnnotate]);

  /**
   * Continue the Robopipe Vision conversation with the labeler's correction.
   *
   * The panel only offers this while a session belongs to the outcome on
   * screen, so the reference here is the live one; the turn replaces exactly
   * the boxes the previous turn added.
   */
  const handleSendVisionFeedback = useCallback(
    (feedback: string) => {
      const session = visionSessionRef.current;
      if (!projectId || session === null) return;
      if (session.taskId !== selectedTaskId) return;
      if (runRequestsRef.current.has(session.taskId)) return;

      const modelType = PreAnnotateModelTypeEnum.DETECTION;
      const taskId = session.taskId;
      const stamp = Date.now();
      const replaceIds = new Set(session.addedIds);

      showRun(taskId, {
        kind: "running",
        modelType,
        startedAt: stamp,
        phase: VisionStreamPhaseEnum.IMAGE,
      });
      const request = claimRun(taskId);
      const { runId } = request;
      const settle = () => releaseRun(taskId, runId);

      request.abort = sendVisionFeedbackStream(
        {
          projectId,
          taskId,
          conversationId: session.conversationId,
          body: { feedback },
        },
        {
        ...previewCallbacks(taskId, runId),
        onDone: (result) => {
          if (!isCurrentRun(taskId, runId)) return;
          const notesChanged = () =>
            appDispatch(
              projectApi.util.invalidateTags([
                { type: ProjectApiTagType.VisionNotes, id: projectId },
              ]),
            );
          // Same as a first turn: staged server-side, waiting on its own task.
          // The session is re-adopted from task detail when the labeler is back.
          if (selectedTaskIdRef.current !== taskId) {
            reportOutcome(
              taskId,
              visionTurnOutcome(result.rectangles.length, result.summary, result.turn),
            );
            if (result.noteId !== null) notesChanged();
            settle();
            return;
          }
          const ids = placeVisionRectangles(result.rectangles, stamp, replaceIds);
          setVisionSession({
            ...session,
            turn: result.turn,
            addedIds: ids,
          });
          // Only while the labeler is looking at the transcript: otherwise the
          // panel is showing this turn's own result card, and the ledger is
          // fetched fresh the next time they open it.
          if (visionTranscriptRef.current !== null) void loadVisionTranscript();
          reportOutcome(
            taskId,
            visionTurnOutcome(ids.length, result.summary, result.turn),
          );
          // The model decided whether the feedback is a rule for the whole
          // project; if so it was saved as a note in the same write as the
          // turn. Say which on the card either way, so the labeller can
          // override it, and let the settings dialog's list catch up — the
          // stream is raw fetch, so no RTK mutation invalidates it.
          setVisionSavedNote({
            taskId,
            turn: result.turn,
            noteId: result.noteId,
            text: result.noteText ?? feedback,
          });
          if (result.noteId !== null) notesChanged();
          settle();
        },
        onError: (message, status) => {
          if (!isCurrentRun(taskId, runId)) return;
          // A conversation the server no longer has (404) or has already
          // closed (409) cannot take another turn. Drop it — the boxes it
          // produced stay on the canvas, and ✨ starts a fresh one. Only if it
          // is still the session on screen: away, task detail re-seeds it.
          if (
            (status === 404 || status === 409) &&
            visionSessionRef.current?.taskId === taskId
          ) {
            visionSessionRef.current = null;
            setVisionSession(null);
          }
          reportOutcome(taskId, { kind: "error", modelType, message });
          settle();
        },
        },
      ).abort;
    },
    [
      projectId,
      selectedTaskId,
      sendVisionFeedbackStream,
      previewCallbacks,
      claimRun,
      releaseRun,
      isCurrentRun,
      appDispatch,
      placeVisionRectangles,
      loadVisionTranscript,
      showRun,
      reportOutcome,
    ],
  );

  /**
   * Exclude a project note from the run card — the chip under a feedback turn,
   * or one in the transcript. The chip flips to "Not saved" (with Save, to put
   * it back) only once the server agrees.
   */
  const handleRemoveVisionNote = useCallback(
    (noteId: number) => {
      if (!projectId) return;
      setVisionNoteError(null);
      setVisionNoteBusy(true);
      void deleteVisionNote({ projectId, noteId })
        .unwrap()
        .then(() => {
          setVisionSavedNote((prev) =>
            prev?.noteId === noteId ? { ...prev, noteId: null } : prev,
          );
          setVisionTranscript((prev) =>
            prev?.map((turn) =>
              turn.note?.id === noteId ? { ...turn, note: null } : turn,
            ) ?? null,
          );
        })
        .catch((err: unknown) => {
          setVisionNoteError(apiErrorMessage(err, t(NOTE_REMOVE_FAILED)));
        })
        .finally(() => setVisionNoteBusy(false));
    },
    [projectId, deleteVisionNote, t],
  );
  /**
   * Save a feedback turn the model kept out of the project notes (or one just
   * excluded), from the result card or the transcript. The server copies the
   * text from the stored turn and makes the caller the author, so they may
   * exclude it again.
   */
  const handleSaveVisionNote = useCallback(
    (turn: number) => {
      const session = visionSessionRef.current;
      if (!projectId || session === null) return;
      setVisionNoteError(null);
      setVisionNoteBusy(true);
      void saveVisionTurnNote({
        projectId,
        taskId: session.taskId,
        conversationId: session.conversationId,
        turn,
      })
        .unwrap()
        .then((note) => {
          setVisionSavedNote((prev) =>
            prev?.turn === turn && prev.taskId === session.taskId
              ? { ...prev, noteId: note.id, text: note.text }
              : prev,
          );
          setVisionTranscript((prev) =>
            prev?.map((entry) =>
              entry.index === turn
                ? { ...entry, note: { id: note.id, userId: note.author?.id ?? null } }
                : entry,
            ) ?? null,
          );
          appDispatch(
            projectApi.util.invalidateTags([
              { type: ProjectApiTagType.VisionNotes, id: projectId },
            ]),
          );
        })
        .catch((err: unknown) => {
          setVisionNoteError(apiErrorMessage(err, t(NOTE_SAVE_FAILED)));
        })
        .finally(() => setVisionNoteBusy(false));
    },
    [projectId, saveVisionTurnNote, appDispatch, t],
  );
  /**
   * Edit the note the last feedback turn became, from its result card. The
   * conversation keeps the feedback as sent; only the project note changes.
   * Resolves whether the server took it, so the card knows to close its editor.
   */
  const handleUpdateVisionNote = useCallback(
    async (noteId: number, text: string): Promise<boolean> => {
      if (!projectId) return false;
      setVisionNoteError(null);
      try {
        const note = await updateVisionNote({ projectId, noteId, text }).unwrap();
        setVisionSavedNote((prev) =>
          prev?.noteId === noteId ? { ...prev, text: note.text } : prev,
        );
        return true;
      } catch (err: unknown) {
        setVisionNoteError(apiErrorMessage(err, t(NOTE_UPDATE_FAILED)));
        return false;
      }
    },
    [projectId, updateVisionNote, t],
  );

  /** Abandon the open image's in-flight auto-annotation. */
  const handleCancelPredict = useCallback(() => {
    const taskId = selectedTaskIdRef.current;
    if (taskId === null) return;
    const request = runRequestsRef.current.get(taskId);
    // Forget it first: the abort rejection checks isCurrentRun and bails out,
    // so the run's own settle() declines to clean up after a cancel. This is
    // that cleanup.
    runRequestsRef.current.delete(taskId);
    request?.abort();
    releasedAtRef.current.set(taskId, Date.now());
    updateRun(taskId, (prev) =>
      prev?.view.kind === "running"
        ? { view: { kind: "cancelled" }, previewBoxes: [] }
        : prev,
    );
    // The conversation stays. Aborting does not stop the Vision model — the
    // turn still finishes and is recorded server-side — so ending the row here
    // would throw away a turn that was paid for, and the labeler can come back
    // and pick it up.
  }, [updateRun]);

  // Leaving the page shouldn't leave a minute of model work streaming into a
  // page that is gone. A Vision turn still finishes server-side, and coming
  // back finds it through the recovery effect below.
  useEffect(() => {
    const requests = runRequestsRef.current;
    return () => {
      for (const request of requests.values()) request.abort();
      requests.clear();
    };
  }, []);

  const navigationGuard = useLabelNavigationGuard({
    isDirty,
    isSaving,
  });

  /**
   * Staged auto-annotations for the image currently open, if any.
   *
   * They render on the canvas as ordinary unsaved shapes but deliberately do
   * NOT set `isDirty` — otherwise every awaiting-review image would throw the
   * leave-confirmation dialog the instant it was clicked, which is exactly the
   * set of images the labeller is trying to triage.
   */
  const stagedAutoAnnotations =
    taskDetail?.id === selectedTaskId ? taskDetail.autoAnnotations : [];

  // Save no longer commits staged predictions — that is Approve's job — so
  // there is only something to save once the labeller has changed the canvas.
  const canSave = isDirty;
  // A run that found nothing leaves no rows, only the task's awaiting-review
  // flag — the banner still has to ask whether the image really is empty.
  const emptyRunAwaitingReview =
    taskDetail?.id === selectedTaskId &&
    taskDetail.awaitingReview === true &&
    stagedAutoAnnotations.length === 0;
  const reviewBannerDismissed =
    stagedAutoAnnotations.length > 0
      ? dismissedReviewIds.has(stagedAutoAnnotations[0].id)
      : selectedTaskId !== null && dismissedEmptyTaskIds.has(selectedTaskId);

  // The labeler's own shapes — saved or drawn, never staged. These are what
  // block an auto-annotate run; staged ones are what a run replaces.
  const ownRectangles = useMemo(
    () => annotations.filter((a) => a.type === "bbox" && !isStagedAnnotation(a)),
    [annotations],
  );

  /**
   * Leaving an image: its card goes if the labeler has seen it settle.
   *
   * A run still going keeps its entry — coming back puts its card up again —
   * and so does one that settles while they are elsewhere, until they have
   * been back to see it. The conversation is not touched either way; the
   * session is re-seeded from the task detail of whatever image they land on.
   */
  const leftTaskIdRef = useRef(selectedTaskId);
  useEffect(() => {
    const left = leftTaskIdRef.current;
    leftTaskIdRef.current = selectedTaskId;
    if (left !== null && left !== selectedTaskId) {
      updateRun(left, (prev) => (prev?.view.kind === "running" ? prev : null));
    }
    setVisionTranscript(null);
    // The saved-note chip goes with the card it belonged to. The note itself
    // stays on the project — the settings dialog still lists it.
    setVisionSavedNote(null);
    setVisionNoteError(null);
  }, [selectedTaskId, updateRun]);

  /**
   * Adopt the conversation the open image already has, if any.
   *
   * Keyed on the task detail rather than the id so it re-runs after a save
   * (the PUT returns a fresh detail), and guarded on the id so a reply for the
   * previous image cannot install its conversation over this one. A live
   * session for this same task keeps its `addedIds` — the sync effect renames
   * them as its boxes become staged rows — and only catches up on turns
   * recorded without it (a recovered run that finished).
   *
   * An adopted session replaces the staged boxes: every turn stages its full
   * set over the previous one, so they are exactly what the server will
   * replace when the next turn lands, and the canvas should do the same.
   */
  useEffect(() => {
    if (!taskDetail || taskDetail.id !== selectedTaskId) return;
    const current = visionSessionRef.current;
    const conversation = taskDetail.visionConversation;
    const stagedIds = taskDetail.autoAnnotations.map((a) => stagedAnnotationId(a.id));
    if (current !== null && current.taskId === taskDetail.id) {
      if (
        conversation !== null &&
        conversation.id === current.conversationId &&
        conversation.turn > current.turn
      ) {
        setVisionSession({ ...current, turn: conversation.turn, addedIds: stagedIds });
      }
      return;
    }
    setVisionSession(
      conversation
        ? {
            taskId: taskDetail.id,
            conversationId: conversation.id,
            turn: conversation.turn,
            addedIds: stagedIds,
          }
        : null,
    );
  }, [taskDetail, selectedTaskId]);

  /**
   * Put back a Robopipe Vision turn this page is not streaming: the labeler
   * reloaded, left the label page mid-run, or started it in another tab.
   *
   * Task detail reports the turn lock. The card shows only the clock — there
   * is no stream to read thoughts or boxes from — and no Cancel, since
   * aborting never stopped the server anyway. Task detail polls while it is up
   * (below); when the lock clears, the card becomes the turn's result and the
   * sync effect brings its staged boxes.
   */
  useEffect(() => {
    if (!taskDetail || taskDetail.id !== selectedTaskId) return;
    const taskId = taskDetail.id;
    // This page's own stream says more than the lock does, for as long as it lasts.
    if (runRequestsRef.current.has(taskId)) return;
    const lockedAt = taskDetail.visionTurnStartedAt;
    const conversation = taskDetail.visionConversation;
    updateRun(taskId, (prev) => {
      if (lockedAt !== null) {
        if (prev?.view.kind === "running") return prev;
        const startedAt = Date.parse(lockedAt);
        const releasedAt = releasedAtRef.current.get(taskId);
        if (releasedAt !== undefined && startedAt < releasedAt + RELEASE_CLOCK_SKEW_MS)
          return prev;
        return {
          view: {
            kind: "running",
            modelType: PreAnnotateModelTypeEnum.DETECTION,
            startedAt,
            detail: t(DETACHED_RUNNING),
          },
          previewBoxes: [],
          detached: { turnBefore: conversation?.turn ?? 0 },
        };
      }
      if (prev?.detached === undefined || prev.view.kind !== "running") return prev;
      // The lock cleared. A turn was recorded exactly when the count moved on.
      if (conversation !== null && conversation.turn > prev.detached.turnBefore) {
        return {
          view: {
            kind: "outcome",
            outcome: visionTurnOutcome(
              taskDetail.autoAnnotations.length,
              undefined,
              conversation.turn,
            ),
          },
          previewBoxes: [],
        };
      }
      return {
        view: {
          kind: "outcome",
          outcome: {
            kind: "error",
            modelType: PreAnnotateModelTypeEnum.DETECTION,
            message: t(DETACHED_FAILED),
          },
        },
        previewBoxes: [],
      };
    });
  }, [taskDetail, selectedTaskId, updateRun, t]);
  // A second subscription to the open image's detail, polling while a
  // recovered run is on screen. RTK Query polls an entry at the shortest
  // interval any subscriber asks for, and structural sharing keeps
  // `taskDetail` the same object until something in it actually changes — so
  // the canvas sync never sees the polls that report nothing new.
  useGetTaskQuery(
    { projectId: projectId!, taskId: selectedTaskId! },
    {
      skip: !projectId || selectedTaskId === null || !isDetachedRunOpen,
      pollingInterval: DETACHED_POLL_MS,
    },
  );

  /**
   * What Robopipe Vision is missing before it can run, if anything: a
   * configured server and a described label.
   */
  const visionNotConfiguredReason = useMemo((): string | undefined => {
    if (!visionAvailable) return t(VISION_UNAVAILABLE);
    return hasUsableDescriptions(labelDescriptions)
      ? undefined
      : t("page.reasons.describeLabel");
  }, [visionAvailable, labelDescriptions, t]);

  /** PENDING, not merely "in the batch": a finished image is free again. */
  const batchClaimsSelectedTask =
    selectedTaskId !== null &&
    batchRunning &&
    batch.itemStatusByTaskId.get(selectedTaskId) ===
      AutoAnnotationBatchItemStatusEnum.PENDING;

  const preAnnotateSegDisabledReason = useMemo(() => {
    if (selectedTaskId === null) return t("page.reasons.selectTask");
    if (batchClaimsSelectedTask) return t(BATCH_CLAIMS_IMAGE_REASON);
    // Vision detects boxes only; the engine, not the canvas, is the blocker.
    return t(VISION_SEGMENTATION_SOON);
  }, [selectedTaskId, batchClaimsSelectedTask, t]);

  const preAnnotateDetDisabledReason = useMemo(() => {
    if (selectedTaskId === null) return t("page.reasons.selectTask");
    if (batchClaimsSelectedTask) return t(BATCH_CLAIMS_IMAGE_REASON);
    if (ownRectangles.length > 0) return t("page.reasons.detectionBlocked");
    return visionNotConfiguredReason;
  }, [
    selectedTaskId,
    batchClaimsSelectedTask,
    ownRectangles.length,
    visionNotConfiguredReason,
    t,
  ]);

  /**
   * Why Feedback is greyed out, or undefined when it is live.
   *
   * Enabled if and only if this image has a conversation to continue — which
   * is a fact about the server, not about the canvas, so it comes from the
   * task detail and survives the labeler going away and coming back.
   */
  const preAnnotateFeedbackDisabledReason = useMemo(() => {
    if (selectedTaskId === null) return t("page.reasons.selectTask");
    if (visionSessionForTask === null) return t("page.reasons.feedbackNothingYet");
    if (isPredicting) return t(PRE_ANNOTATE_BUSY_REASON);
    return undefined;
  }, [selectedTaskId, visionSessionForTask, isPredicting, t]);

  // ── Run panel wiring ───────────────────────────────────────────────────────
  // Every run this page streams can be stopped. A recovered one has no request behind it to stop.
  // Built at render time from the view, never stored in state.
  const handleRunPanelCancel =
    isPredicting && !isDetachedRunOpen ? handleCancelPredict : undefined;

  /**
   * Closing the card hides it and drops the transcript it loaded; it does not
   * end the Vision conversation.
   */
  const handleDismissRunPanel = useCallback(
    (view: PreAnnotateRunView) => {
      dismissRunPanel(view);
      // The conversation stays open. Dismissing is a "get this off my canvas"
      // gesture, not a decision to end a thread — and ending it here would
      // silently disable Feedback with nothing on screen to explain why.
      // The toolbar's Feedback button is the way back to it.
      setVisionTranscript(null);
    },
    [dismissRunPanel],
  );

  /**
   * Every Approve — the header's, the awaiting-review banner's and the run
   * card's: approve-and-save, then the run card goes away, since what it
   * offered to approve is done. A failed save keeps the card up so the labeler
   * can try again, and a run still in flight keeps its card.
   */
  const handleApproveRun = useCallback(async () => {
    const view = runView;
    try {
      await handleApprove();
    } catch (err) {
      toast.error(apiErrorMessage(err, t("page.saveAnnotationsFailed")));
      return;
    }
    if (view !== null && view.kind !== "running") handleDismissRunPanel(view);
  }, [runView, handleApprove, handleDismissRunPanel, t]);

  // ✨ runs the project's default mode and nothing else.
  const preAnnotateDefaultDisabledReason =
    preAnnotateDefaultMode === PreAnnotateModelTypeEnum.DETECTION
      ? preAnnotateDetDisabledReason
      : preAnnotateSegDisabledReason;

  // Same gate as the toolbar's ✨ for that geometry, so the button is
  // never enabled where handlePreAnnotate would silently return.
  const runAgainDisabledReason =
    runView?.kind !== "outcome"
      ? undefined
      : isPredicting
        ? t(PRE_ANNOTATE_BUSY_REASON)
        : isSaving
          ? t("common:actions.saving")
          : runView.outcome.modelType === PreAnnotateModelTypeEnum.DETECTION
            ? preAnnotateDetDisabledReason
            : preAnnotateSegDisabledReason;

  useLabelShortcuts({
    tasks,
    selectedTaskId,
    page,
    totalPages,
    labels,
    activeLabel,
    isDirty,
    canSave,
    isSaving,
    annotationCount: annotations.length,
    onSave: handleSave,
    onSaveEmpty: handleSaveEmpty,
    onSetToolMode: setToolMode,
    onToggleCrosshair: toggleCrosshair,
    onResetView: handleResetView,
    onSelectTask: setSelectedTaskId,
    onChangePage: setPage,
    onSelectLabel: handleSelectLabel,
    onHidePreviewDown: startPreviewHideAll,
    onHidePreviewUp: stopPreviewHideAll,
  });

  useAnnotationNudge({
    annotations,
    // Nudge is a GT edit — disable it entirely in the read-only inferred view
    // so a stale GT selection can't be moved invisibly.
    selectedAnnotationIds:
      activeAnnotationTab === "inferred"
        ? EMPTY_SELECTION
        : selectedAnnotationIds,
    toolMode,
    imageDimsRef,
    setAnnotations: setAnnotationsAndDirty,
    pushBatchEntry: history.pushBatchEntry,
    startNudge: (ids) => canvasRef.current?.startNudge(ids),
    applyNudge: (dx, dy) => canvasRef.current?.applyNudge(dx, dy),
    clearNudge: () => canvasRef.current?.clearNudge(),
  });

  const isolatedAnnotationId = useMemo(() => {
    if (isolatedLabelId !== null) return null;
    if (
      hiddenAnnotationIds.size === annotations.length - 1 &&
      annotations.length > 0
    ) {
      return (
        annotations.find((a) => !hiddenAnnotationIds.has(a.id))?.id ?? null
      );
    }
    return null;
  }, [hiddenAnnotationIds, annotations, isolatedLabelId]);

  // A fresh run on this image hides the staged shapes it is about to
  // replace. Derived from the run view, never stored: success removes them,
  // while a failure or a cancel settles the view and they are back.
  const hidingStaged = runView?.kind === "running" && runView.replacesStaged === true;
  const runHiddenIds = useMemo(
    () =>
      hidingStaged
        ? new Set([...hiddenAnnotationIds, ...annotations.filter(isStagedAnnotation).map((a) => a.id)])
        : hiddenAnnotationIds,
    [hidingStaged, hiddenAnnotationIds, annotations],
  );

  // Priority: hold-to-hide (h) > a run replacing the staged shapes > class
  // isolate > per-annotation hides.
  const visibleAnnotations = useMemo(() => {
    if (previewHideAll) return [];
    const shown = hidingStaged ? annotations.filter((a) => !isStagedAnnotation(a)) : annotations;
    if (isolatedLabelId !== null) {
      return shown.filter((a) => a.labelId === isolatedLabelId);
    }
    return shown.filter((a) => !hiddenAnnotationIds.has(a.id));
  }, [annotations, hiddenAnnotationIds, previewHideAll, isolatedLabelId, hidingStaged]);

  // If the isolated class loses all its annotations (e.g. user deleted them),
  // drop the filter so the canvas isn't stuck empty.
  useEffect(() => {
    if (isolatedLabelId === null) return;
    const stillExists = annotations.some((a) => a.labelId === isolatedLabelId);
    if (!stillExists) setIsolatedLabelId(null);
  }, [annotations, isolatedLabelId]);

  const activeLabelForCanvas = useMemo(
    () =>
      activeLabel
        ? {
            id: String(activeLabel.id),
            name: activeLabel.name,
            color: activeLabel.color,
          }
        : null,
    [activeLabel],
  );

  // When on the Inferred tab the canvas shows only inference predictions
  // (read-only, dashed), optionally with the GT annotations overlaid (solid).
  const isInferredView = showInDataset && activeAnnotationTab === "inferred";
  const canvasAnnotations = useMemo(() => {
    if (!isInferredView) return visibleAnnotations;
    // Detection model evaluated against polygon GT: the job matched each
    // polygon as its bounding box, so overlay the boxes the metrics were
    // actually computed from rather than the original polygons.
    const gtOverlay = !showGtOverlay
      ? []
      : confidenceReport?.modelType === PreAnnotateModelTypeEnum.DETECTION &&
          confidenceReport.gtGeometry === ConfidenceReportGtGeometryEnum.POLYGON
        ? visibleAnnotations.map(polygonAnnotationToBbox)
        : visibleAnnotations;
    return [...gtOverlay, ...inferredAnnotations];
  }, [
    isInferredView,
    visibleAnnotations,
    inferredAnnotations,
    showGtOverlay,
    confidenceReport?.modelType,
    confidenceReport?.gtGeometry,
  ]);

  return (
    <div className="-m-6 grid min-h-0 flex-1 grid-cols-[320px_280px_1fr] grid-rows-[minmax(0,1fr)] bg-white">
      <DataSourcePanel
        tasks={tasks}
        selectedTaskId={selectedTaskId}
        annotationCount={annotations.length}
        onSelectTask={setSelectedTaskId}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        filter={filter}
        labels={labels}
        sort={sort}
        onSortFilterChange={handleSortFilterChange}
        metricsAvailable={metricsAvailable}
        showTaskMetrics={showInDataset}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectPage={handleToggleSelectPage}
        onClearSelection={clearSelection}
        batchItemStatus={batch.itemStatusByTaskId}
        runningTaskIds={runningTaskIds}
        batchRunning={batchRunning || batch.isStarting}
        onAutoAnnotate={() => handleRunBatch([...selectedIds])}
        onOpenAutoAnnotateSettings={() => setPreAnnotateOpen(true)}
      />
      <AnnotationPanel
        annotations={annotations}
        labels={labels}
        selectedAnnotationIds={selectedAnnotationIds}
        onSelectAnnotation={handleSelectFromSidebar}
        onDeleteAnnotation={deleteAnnotationWithSelection}
        onReorderAnnotations={handleReorderAnnotations}
        onMoveAnnotation={handleMoveAnnotationInSidebar}
        onUngroupAnnotations={handleUngroupAnnotations}
        hiddenAnnotationIds={runHiddenIds}
        onToggleAnnotationVisibility={toggleAnnotationVisibility}
        onToggleAllAnnotationsVisibility={toggleAllAnnotationsVisibility}
        isolatedLabelId={isolatedLabelId}
        onIsolateLabel={setIsolatedLabel}
        onClearIsolate={clearIsolatedLabel}
        historyEntries={history.entries}
        historyIndex={history.currentIndex}
        onJumpTo={history.jumpTo}
        isLoadingLabels={isLoadingLabels}
        projectId={projectId}
        taskId={selectedTaskId}
        isolatedAnnotationId={isolatedAnnotationId}
        onIsolateAnnotation={isolateAnnotation}
        activeTab={activeAnnotationTab}
        onTabChange={setActiveAnnotationTab}
        showInferredTab={showInDataset}
        inferredRegions={rawRegions}
        isLoadingRegions={isLoadingRegions}
        reportStatus={confidenceReport?.status as ConfidenceReportStatusEnum | null ?? null}
        reportGtGeometry={confidenceReport?.gtGeometry ?? null}
        showGtOverlay={showGtOverlay}
        onToggleGtOverlay={setShowGtOverlay}
        selectedInferredRegionId={selectedInferredRegionId}
        onSelectInferredRegion={handleSelectInferredRegion}
      />
      <div className="relative flex min-h-0 flex-col overflow-hidden">
        <Canvas
          ref={canvasRef}
          task={selectedTask ?? taskDetail}
          readOnly={isInferredView}
          annotations={canvasAnnotations}
          selectedAnnotationIds={
            isInferredView ? inferredSelectionSet : selectedAnnotationIds
          }
          primarySelectedId={
            isInferredView ? selectedInferredId : primarySelectedId
          }
          toolMode={toolMode}
          activeLabel={activeLabelForCanvas}
          scale={canvasState.scale}
          position={canvasState.position}
          canSave={canSave}
          isSaving={isSaving}
          onSave={handleSave}
          onSaveEmpty={handleSaveEmpty}
          showCrosshair={showCrosshair}
          previewBoxes={previewBoxes}
          awaitingReview={stagedAutoAnnotations.length > 0}
          onApprove={() => void handleApproveRun()}
          onSelect={isInferredView ? handleInferredSelect : handleSelect}
          onAddAnnotation={history.addAnnotation}
          onUpdateAnnotation={history.updateAnnotation}
          onDeleteSelected={handleClear}
          onCopySelection={handleCopySelection}
          onPasteClipboard={handlePasteClipboard}
          onGroupSelected={handleGroupSelected}
          onUngroupSelected={() => {
            const groupIds = new Set(
              Array.from(selectedAnnotationIds)
                .map((id) => annotations.find((a) => a.id === id)?.groupId)
                .filter(Boolean) as string[],
            );
            if (groupIds.size > 0) handleUngroupAnnotations(groupIds);
          }}
          canGroup={canGroup}
          onGroupTranslate={handleGroupTranslate}
          onUndo={history.undo}
          onRedo={history.redo}
          onZoomAtPoint={canvasState.zoomAtPoint}
          onSetPosition={canvasState.setPosition}
          onFitImage={canvasState.fitImage}
          onImageLoad={(w, h) => {
            imageDimsRef.current = { width: w, height: h };
          }}
        />
        <div className="pointer-events-none absolute right-2 top-1/2 z-10 -translate-y-1/2">
          <div className="pointer-events-auto">
            <Toolbar
              toolMode={toolMode}
              onSetToolMode={setToolMode}
              onUndo={history.undo}
              onRedo={history.redo}
              onZoomIn={canvasState.zoomIn}
              onZoomOut={canvasState.zoomOut}
              onClear={handleClear}
              canUndo={history.canUndo}
              canRedo={history.canRedo}
              hasSelection={selectedAnnotationIds.size > 0}
              hasLabels={labels.length > 0 || isLoadingLabels}
              showCrosshair={showCrosshair}
              onToggleCrosshair={toggleCrosshair}
              onResetView={handleResetView}
              onPreAnnotate={() => requestPreAnnotate(preAnnotateDefaultMode)}
              onOpenPreAnnotateSettings={() => setPreAnnotateOpen(true)}
              preAnnotateDisabled={
                preAnnotateDefaultDisabledReason !== undefined || isPredicting || isSaving
              }
              preAnnotateDisabledReason={preAnnotateDefaultDisabledReason}
              preAnnotatePending={isPredicting}
              onPreAnnotateFeedback={handleResumeVision}
              preAnnotateFeedbackDisabledReason={
                preAnnotateFeedbackDisabledReason
              }
              canGroup={canGroup}
              onGroupSelected={handleGroupSelected}
            />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-2 bottom-2 z-10 flex justify-center">
          <div className="pointer-events-auto min-w-0 max-w-full">
            <ClassSelect
              labels={labels}
              activeLabelId={chipDisplayedLabelId}
              projectId={projectId}
              hasUnsavedChanges={isDirty}
              onSelectLabel={handleSelectLabel}
              onCreateLabel={handleCreateLabel}
              onUpdateLabel={handleUpdateLabel}
              onArchiveLabel={handleArchiveLabel}
              onRestoreLabel={handleRestoreLabel}
              onGenerateDescription={
                visionAvailable ? handleGenerateDescription : undefined
              }
              isLoadingLabels={isLoadingLabels}
            />
          </div>
        </div>
        {/* Only when the transient run card is not on screen: it already says
            what just landed, and the two would sit on top of each other. This
            banner is for coming back to the image later. */}
        {runView === null && visibleBatch === null && !reviewBannerDismissed && (
          <AwaitingReviewBanner
            autoAnnotations={stagedAutoAnnotations}
            emptyRun={emptyRunAwaitingReview}
            onApprove={() =>
              void (emptyRunAwaitingReview
                ? handleSave({ approve: true, reviewed: true })
                : handleApproveRun())
            }
            onDismiss={() =>
              emptyRunAwaitingReview
                ? setDismissedEmptyTaskIds((prev) =>
                    new Set(prev).add(selectedTaskId!),
                  )
                : setDismissedReviewIds((prev) =>
                    new Set(prev).add(stagedAutoAnnotations[0].id),
                  )
            }
            isSaving={isSaving}
          />
        )}
        {/*
            Batch progress sits above the single-run affordances and hides
            them: while a batch is live the ✨ panel has nothing to say, and
            two floating cards in the same corner would overlap.
        */}
        {visibleBatch !== null && (
          <BatchRunCard
            batch={visibleBatch}
            onDismiss={() => setDismissedBatchId(visibleBatch.id)}
            onRetryFailed={handleRunBatch}
          />
        )}
        {runView !== null && visibleBatch === null && (
          <PreAnnotateRunPanel
            // One card per image: another task's card must not inherit this
            // one's half-typed feedback or open editor.
            key={selectedTaskId}
            view={runView}
            onDismiss={handleDismissRunPanel}
            onCancel={handleRunPanelCancel}
            onRunAgain={() => {
              if (runView.kind === "outcome")
                requestPreAnnotate(runView.outcome.modelType);
            }}
            runAgainDisabledReason={runAgainDisabledReason}
            onApprove={() => void handleApproveRun()}
            approveDisabledReason={
              isPredicting
                ? t(PRE_ANNOTATE_BUSY_REASON)
                : isSaving
                  ? t("common:actions.saving")
                  : undefined
            }
            // Only while the conversation that produced this very outcome is
            // still live: its absence is what hides the feedback affordance.
            onSendFeedback={
              visionSessionForTask !== null &&
              (runView.kind === "resumed" || runView.kind === "outcome")
                ? handleSendVisionFeedback
                : undefined
            }
            feedbackDisabledReason={
              isPredicting ? t(PRE_ANNOTATE_BUSY_REASON) : undefined
            }
            // What became of the last feedback turn's note — shown only on the
            // card of the turn it describes.
            savedNote={
              visionSavedNote !== null && visionSavedNote.taskId === selectedTaskId
                ? visionSavedNote
                : null
            }
            noteError={visionNoteError}
            noteBusy={visionNoteBusy}
            onRemoveNote={handleRemoveVisionNote}
            onSaveNote={handleSaveVisionNote}
            onUpdateNote={handleUpdateVisionNote}
            // Only on a card the labeler opened with Feedback — a card that
            // just reported a run they watched happen needs no history.
            transcript={
              runView.kind === "resumed" || runView.kind === "outcome"
                ? visionTranscript
                : null
            }
            transcriptLoading={visionTranscriptLoading}
            onOpenSettings={() => setPreAnnotateOpen(true)}
          />
        )}
      </div>
      <PreAnnotateSettingsDialog
        projectId={projectId}
        open={preAnnotateOpen}
        onOpenChange={setPreAnnotateOpen}
        settings={preAnnotateSettings}
        isLoadingSettings={isLoadingPreAnnotateSettings}
        projectLabels={labels}
        lastRun={lastRunForTask}
        onSave={updatePreAnnotateSettings}
        isSaving={isSavingPreAnnotateSettings}
        onOpenTask={setSelectedTaskId}
        onGenerateDescription={handleGenerateDescription}
      />
      <LeaveAnnotationsDialog
        open={navigationGuard.reason === "dirty"}
        onCancel={navigationGuard.reset}
        onDiscard={navigationGuard.proceed}
        onSave={handleSave}
      />
      <ReplaceAutoAnnotationsDialog
        open={pendingReplaceRun !== null}
        stagedCount={stagedOnCanvasCount}
        discardsTurns={runDiscardsTurns}
        onConfirm={handleConfirmReplaceRun}
        onCancel={() => setPendingReplaceRun(null)}
      />
    </div>
  );
};
